// frontend/src/pages/staff/StaffBookings.js
// TODO FEATURE REQUEST: Add booking import functionality
// - Allow professionals to import past bookings from CSV/Excel
// - Similar to service import feature (see StaffServices.js)
// - Useful for professionals migrating from other booking systems
// - Fields: customer name, phone, email, service, date, time, status, payment
// - Add "Import Bookings" button near filter controls
// - Validate dates, times, and customer info before import
// - Create sample CSV template (sample-bookings.csv in public folder)
// - Link to booking history/analytics for migrated data
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StaffSidebarLayout from "./StaffSidebarLayout";
import axios from "axios";
import WalkInButton from "../../components/WalkInButton";
import "./staffStyles.css";
import AddLookModal from "../../components/AddLookModal";
import {
  formatTimeWithTimezone,
  isSameTimezone,
  formatTime12Hour,
} from "../../utils/timezoneUtils";

// P0a (F3 — service selection in bundles/groups): provider self-booking is paused
// until the pricing-engine fix lands. Set to `true` (phase P10) to restore the
// "+ Add New Booking" button, the empty-state "Create First Booking" button AND
// the per-booking "✏️ Edit" button — the edit path also changes the service/date
// without going through the pricing engine.
const PROVIDER_SELF_BOOKING_ENABLED = false;

// Test booking badge component
const TestBadge = ({ isTest }) => {
  if (!isTest) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        marginLeft: "8px",
        fontSize: "10px",
        fontWeight: "600",
        borderRadius: "4px",
        backgroundColor: "#f59e0b",
        color: "white",
        verticalAlign: "middle",
      }}
    >
      TEST
    </span>
  );
};

function StaffBookings() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  // The professional's zone is the shop location's zone (R2/R3 inherit it) or the
  // R4/R7's own chosen zone. Never guessed from the browser and never defaulted; it
  // is learned from the bookings API response (`professionalTimezone`).
  const [professionalTimezone, setProfessionalTimezone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewBookingForm, setShowNewBookingForm] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Filter states - default to today so professionals see what's coming up today first
  const [filterStatus, setFilterStatus] = useState("today");
  const [filterDate, setFilterDate] = useState("all");
  const [activeTab, setActiveTab] = useState("all");

  // Booking form states
  const [services, setServices] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [formData, setFormData] = useState({
    serviceId: "",
    bookingType: "walk-in",
    customerFirstName: "",
    customerLastName: "",
    customerPhone: "",
    customerEmail: "",
    appointmentDate: "",
    startTime: "",
    customerNotes: "",
    paymentMethod: "pending",
    tipAmount: 0,
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // View/Edit booking states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editFormData, setEditFormData] = useState({
    serviceId: "",
    appointmentDate: "",
    startTime: "",
    customerNotes: "",
    paymentMethod: "pending",
    tipAmount: 0,
  });

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Cancellation request states
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false);
  const [cancelRequestBooking, setCancelRequestBooking] = useState(null);
  const [cancelRequestReason, setCancelRequestReason] = useState("");
  const [cancelRequestSubmitting, setCancelRequestSubmitting] = useState(false);

  /**
   * HAND-OFF (2026-10-24). A professional may ask a colleague to take a booking, but the booking moves only when
   * that colleague accepts — so this screen needs both halves: choosing someone to ask, and answering a request
   * that was sent to you.
   */
  const [showHandoffModal, setShowHandoffModal] = useState(false);
  const [handoffBooking, setHandoffBooking] = useState(null);
  const [handoffCandidates, setHandoffCandidates] = useState([]);
  const [handoffTargetId, setHandoffTargetId] = useState("");
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoffNote, setHandoffNote] = useState("");
  const [handoffError, setHandoffError] = useState("");
  // Hand-offs offered TO this professional — the other half of "the receiving R3 must accept".
  const [incomingHandoffs, setIncomingHandoffs] = useState([]);

  // Mark as paid states
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidBooking, setMarkPaidBooking] = useState(null);
  const [markPaidData, setMarkPaidData] = useState({
    paymentType: "cash",
    amount: "",
    notes: "",
  });
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);

  // Add Look states
  const [looksEligibleIds, setLooksEligibleIds] = useState(null); // null = not loaded yet
  // Map of bookingId -> existingLook { lookId, status, imageCount } for add-to-existing mode
  const [looksExistingMap, setLooksExistingMap] = useState({});
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [addLookBooking, setAddLookBooking] = useState(null);

  // Record product used states
  const [usageBooking, setUsageBooking] = useState(null);
  const [usageHoldings, setUsageHoldings] = useState([]);
  const [usageLines, setUsageLines] = useState({}); // itemId -> quantity string
  const [usagePrefilled, setUsagePrefilled] = useState([]); // itemIds that came from last time
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageSaving, setUsageSaving] = useState(false);
  const [usageError, setUsageError] = useState("");
  const [containersHeld, setContainersHeld] = useState(0);
  /**
   * SHARED PRODUCTS ARE RECORDED BY COUNTING THE JOB ONTO A CONTAINER — never by typing an amount.
   *
   * `usageSharedHoldings` is what the professional is holding of a shared product (so the modal can
   * offer to open one), and `usageContainers` is their OPEN containers with the jobs each covers.
   * The divisor IS the tick count, so this section asks one question: was this client one of them?
   */
  const [usageSharedHoldings, setUsageSharedHoldings] = useState([]);
  const [usageContainers, setUsageContainers] = useState([]);
  const [usageContainerBusy, setUsageContainerBusy] = useState(null); // bottle id / item id
  /**
   * WHAT IS ALREADY ON THIS APPOINTMENT.
   *
   * `consume-batch` has no "already recorded" guard, so pressing Save twice wrote the usage twice and
   * the cost report counted it twice. The amounts are shown before the fields rather than blocked, so
   * a genuine correction is still possible — but nobody can add a second full measure by not noticing.
   */
  const [usageRecorded, setUsageRecorded] = useState([]);
  // What this professional is currently holding, for the summary line on this page.
  const [myHoldings, setMyHoldings] = useState([]);

  // Migration states (Option B: R1 → R3 booking migration)
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [migrationCount, setMigrationCount] = useState(0);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // All professionals use unified professional role type
  const getRoleType = useCallback(() => {
    return "professional";
  }, []);

  /**
   * WHO MAY BE OFFERED "RECORD PRODUCT USED".
   *
   * Mirrors `mayRecordUsage()` on the server (`routes/serviceInventoryRoutes.js`): usage is recorded
   * by the person who DELIVERED the service — an R3 at the chair, or an independent R4/R7 using
   * their own stock. The desk is excluded there because an R2 (or an owner acting as the desk) was
   * not in the room, so an entry they create records nothing that happened.
   *
   * The server refuses it either way; without this gate the desk gets a button whose only outcome
   * is a 403 banner, which reads as a broken feature rather than a rule.
   *
   * Read from the ACTIVE role (`ogestyle_role_override` then `ogestyle_role`), the same source
   * `ProtectedRoute` uses — NOT from the role list. An owner who also holds R3 has "R3" in their
   * list the whole time, and a check on the list would offer them the button while they are
   * signed in as the owner.
   */
  const canRecordProductUsed = useCallback(() => {
    const activeRole =
      localStorage.getItem("ogestyle_role_override") ||
      localStorage.getItem("ogestyle_role") ||
      "";
    return [
      "R3",
      "R4",
      "R7",
      "professional",
      "freelancer",
      "booth-renter",
    ].includes(activeRole);
  }, []);

  // Security: Get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  // Security: Validate API base URL
  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error. Please contact support.");
      return null;
    }
    return baseURL;
  }, []);

  // Helper: Format date
  const formatDate = (dateString) => {
    // dateString is now in YYYY-MM-DD format - parse at noon to avoid timezone issues
    const date = new Date(dateString + "T12:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDateTimePartsInTimezone = useCallback(
    (date) => {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: professionalTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).formatToParts(date);

      return parts.reduce((acc, part) => {
        if (part.type !== "literal") acc[part.type] = part.value;
        return acc;
      }, {});
    },
    [professionalTimezone],
  );

  const formatDateForTimezone = useCallback(
    (date) => {
      const parts = getDateTimePartsInTimezone(date);
      return `${parts.year}-${parts.month}-${parts.day}`;
    },
    [getDateTimePartsInTimezone],
  );

  const getTodayStringInProfessionalTimezone = useCallback(
    () => formatDateForTimezone(new Date()),
    [formatDateForTimezone],
  );

  const getCancellationPayoutAmount = (booking) => {
    if (!booking) return null;
    if (booking.status !== "cancelled" && booking.status !== "canceled") {
      return null;
    }

    const distribution = booking.cancellationFeeDistribution;
    if (!distribution) return null;

    const canonical = Number(booking.cancellationPayoutAmount) || 0;
    if (canonical > 0) return canonical;

    // The professional's shares ADDED, the way the payout calculator reads them
    // (utils/paymentSplitCalculator.js). ownerShare is deliberately excluded - it is the
    // BUSINESS's share, and treating it as the professional's payout is how this list could
    // disagree with what they are actually paid.
    const payoutAmount =
      (Number(distribution.freelancerShare) || 0) +
      (Number(distribution.professionalShare) || 0);

    return payoutAmount > 0 ? payoutAmount : null;
  };

  // Helper: Render time with timezone info (shows dual timezone if customer is in different timezone)
  const renderTimeWithTimezone = (booking) => {
    const staffTz =
      booking.staffTimezone || booking.shopTimezone || professionalTimezone;
    const customerTz = booking.customerTimezone;
    // appointmentDate is already in YYYY-MM-DD string format
    const appointmentDateStr = booking.appointmentDate;

    const staffTimeStart = formatTimeWithTimezone(
      booking.startTime,
      appointmentDateStr,
      staffTz,
    );
    const staffTimeEnd = formatTimeWithTimezone(
      booking.endTime,
      appointmentDateStr,
      staffTz,
    );

    // If customer timezone exists and is different, show both
    if (customerTz && !isSameTimezone(staffTz, customerTz)) {
      const customerTimeStart = formatTimeWithTimezone(
        booking.startTime,
        appointmentDateStr,
        customerTz,
      );
      const customerTimeEnd = formatTimeWithTimezone(
        booking.endTime,
        appointmentDateStr,
        customerTz,
      );

      return (
        <div>
          <div>
            🕐 {staffTimeStart} - {staffTimeEnd}
          </div>
          <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "2px" }}>
            Customer: {customerTimeStart} - {customerTimeEnd}
          </div>
        </div>
      );
    }

    // Same timezone or no customer timezone - show simple format
    return (
      <div>
        🕐 {formatTime12Hour(booking.startTime)} -{" "}
        {formatTime12Hour(booking.endTime)}
        <span style={{ marginLeft: "8px", color: "#999" }}>
          ({booking.duration} min)
        </span>
      </div>
    );
  };

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    // Hand-offs offered to this professional. Loaded with the list so the panel is never stale.
    (async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const response = await axios.get(
          `${baseURL}/api/bookings/reassign-requests/incoming`,
          { headers },
        );
        setIncomingHandoffs(
          Array.isArray(response.data?.requests) ? response.data.requests : [],
        );
      } catch {
        // A failed panel must not stop the booking list from rendering.
      }
    })();

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Calculate date range based on filter. The range is expressed in the
      // professional's zone, so until the API response has told us that zone we fetch
      // WITHOUT a date filter rather than guessing one from the browser. Learning the
      // zone changes this callback's identity, which re-runs the mount effect and
      // applies the filter on the next pass.
      let startDate, endDate;
      const today = new Date();
      const todayInProfessionalTimezone = professionalTimezone
        ? getTodayStringInProfessionalTimezone()
        : null;

      if (!professionalTimezone) {
        // No zone yet - fetch unfiltered once and pick up `professionalTimezone`.
      } else if (filterStatus === "today") {
        startDate = todayInProfessionalTimezone;
        endDate = startDate;
      } else if (filterDate === "upcoming") {
        startDate = todayInProfessionalTimezone;
        endDate = formatDateForTimezone(
          new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()),
        );
      } else if (filterDate === "past") {
        endDate = todayInProfessionalTimezone;
        startDate = formatDateForTimezone(
          new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()),
        );
      } else if (filterDate === "today") {
        startDate = todayInProfessionalTimezone;
        endDate = startDate;
      } else if (filterDate === "month") {
        // Current month - matches dashboard
        const nowParts = getDateTimePartsInTimezone(new Date());
        const year = Number(nowParts.year);
        const month = Number(nowParts.month);
        const lastDay = String(new Date(year, month, 0).getDate()).padStart(2, "0");
        startDate = `${year}-${nowParts.month}-01`;
        endDate = `${year}-${nowParts.month}-${lastDay}`;
      }

      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      // Only send real booking statuses to the API — "today" and "upcoming" are client-side filters
      const validApiStatuses = [
        "pending",
        "confirmed",
        "completed",
        "cancelled",
        "no-show",
      ];
      if (filterStatus !== "all" && validApiStatuses.includes(filterStatus)) {
        params.append("status", filterStatus);
      }

      const res = await axios.get(`${baseURL}/api/bookings/staff?${params}`, {
        headers,
      });
      // Find any bookings with cancelRequest and log them separately
      const bookingsWithCancelRequest = res.data.bookings?.filter(
        (b) => b.cancellationRequest,
      );
      if (bookingsWithCancelRequest?.length > 0) {
      }

      setBookings(res.data.bookings || []);
      if (res.data.professionalTimezone) {
        setProfessionalTimezone(res.data.professionalTimezone);
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message ||
            "Unable to load bookings. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    getAuthHeaders,
    getApiBaseUrl,
    navigate,
    filterStatus,
    filterDate,
    getTodayStringInProfessionalTimezone,
    formatDateForTimezone,
    getDateTimePartsInTimezone,
  ]);

  // Update booking status
  const updateBookingStatus = async (bookingId, status, reason = "") => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/bookings/${bookingId}/status`,
        { status, cancellationReason: reason },
        { headers },
      );

      setSuccessMsg(`Booking ${status} successfully!`);
      fetchBookings(); // Refresh list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message || "Unable to update booking status.",
      );
      setTimeout(() => setErrorMsg(""), 5000);
    }
  };

  /**
   * RECORD WHAT WAS USED, FROM THE APPOINTMENT THAT USED IT.
   *
   * Product cost per service only exists if consumption is tied to a booking, and the only place
   * that link could be made was the Service Inventory page — a page a stylist has no reason to open
   * after a client. So the recording happens here instead, at the moment they know the answer.
   *
   * What is offered comes from what they actually HOLD (`/mine`), not from the whole catalogue: a
   * stylist cannot use a bottle that was never handed to them, and a list of everything the
   * business owns would be an invitation to guess.
   *
   * A SHARED PRODUCT IS OFFERED TOO, but not as an amount. Ticking one of these clients onto an open
   * container is the ONLY correct way to say "this product was used on this job": the container's
   * cost is divided by however many jobs it covers, so recording it here as a quantity would charge
   * one client for a container that served several — which `consume-batch` refuses on purpose. Until
   * 2026-10-22 the modal simply withheld containers with a note telling the reader to go and finish
   * it somewhere else, which meant the professional had to leave the appointment for a page they
   * have no reason to open.
   *
   * The amounts arrive PREFILLED from the last time this service was delivered, so the common case
   * is "press Save", and the field stays editable for the case where it is not.
   */
  const openUsageFor = async (booking) => {
    setUsageBooking(booking);
    setUsageError("");
    setUsageLines({});
    setUsagePrefilled([]);
    setUsageHoldings([]);
    setContainersHeld(0);
    setUsageSharedHoldings([]);
    setUsageContainers([]);
    setUsageRecorded([]);
    setUsageContainerBusy(null);
    setUsageLoading(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const [mineRes, lastUsedRes, bottlesRes] = await Promise.all([
        axios.get(`${baseURL}/api/service-inventory/mine`, { headers }),
        axios
          .get(`${baseURL}/api/service-inventory/last-used`, {
            params: { serviceId: booking.serviceId?._id || booking.serviceId },
            headers,
          })
          .catch(() => ({ data: {} })),
        // Fails soft: not being able to list containers must not stop a per-appointment product
        // from being recorded, which is the flow that worked before this section existed.
        axios
          .get(`${baseURL}/api/service-inventory/bottles`, {
            params: { status: "open" },
            headers,
          })
          .catch(() => ({ data: {} })),
      ]);

      const holdings = Array.isArray(mineRes.data?.holdings) ? mineRes.data.holdings : [];
      const perAppointment = holdings.filter((row) => row.usageMode !== "shared");
      const shared = holdings.filter((row) => row.usageMode === "shared");
      setContainersHeld(shared.length);
      setUsageHoldings(perAppointment);
      setUsageSharedHoldings(shared);

      const bottles = Array.isArray(bottlesRes.data?.bottles)
        ? bottlesRes.data.bottles
        : [];
      // Open ones only, and only for products this professional actually holds — a container they
      // cannot have opened is not theirs to tick.
      setUsageContainers(
        bottles.filter(
          (row) => row.isOpen !== false && (row.canChangeJobs ?? true),
        ),
      );

      /**
       * WHAT IS ALREADY ON THIS APPOINTMENT. `/mine` returns this professional's recent movements
       * with the appointment each one names, so the same call answers it — no new endpoint, and the
       * figure comes from the ledger rather than being remembered by the screen.
       */
      const movements = Array.isArray(mineRes.data?.movements)
        ? mineRes.data.movements
        : [];
      const bookingId = String(booking._id);
      setUsageRecorded(
        movements
          .filter(
            (row) =>
              row.type === "consume" &&
              row.appointment &&
              String(row.appointment._id || "") === bookingId,
          )
          .map((row) => ({
            movementId: String(row._id),
            itemId: row.itemId ? String(row.itemId) : null,
            itemName: row.itemName || "Product",
            quantity: row.quantity,
            unit: row.unit,
          })),
      );

      const suggestions = Array.isArray(lastUsedRes.data?.suggestions)
        ? lastUsedRes.data.suggestions
        : [];
      const next = {};
      const fromLastTime = [];
      for (const row of perAppointment) {
        const suggestion = suggestions.find(
          (s) => String(s.itemId) === String(row.itemId),
        );
        if (suggestion) {
          next[row.itemId] = String(suggestion.quantity);
          fromLastTime.push(row.itemId);
        }
      }
      setUsageLines(next);
      setUsagePrefilled(fromLastTime);
    } catch (err) {
      setUsageError(
        err?.response?.data?.message || "Unable to load the products you are holding.",
      );
    } finally {
      setUsageLoading(false);
    }
  };

  /**
   * SAY THIS JOB WAS ONE THE CONTAINER SERVED.
   *
   * No amount is sent, because there is nothing to send: the divisor is the list of jobs. Adding the
   * same job twice is refused by the endpoint as `already_covered`, so a double-click cannot
   * silently halve what every other client on that container carries.
   */
  const countBookingOnContainer = async (bottleId) => {
    if (!usageBooking || !bottleId) return;
    setUsageContainerBusy(String(bottleId));
    setUsageError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.post(
        `${baseURL}/api/service-inventory/bottles/${bottleId}/appointments`,
        { appointmentRefs: [usageBooking._id] },
        { headers },
      );

      const updated = res.data?.bottle;
      if (updated) {
        setUsageContainers((prev) =>
          prev.map((row) =>
            String(row._id) === String(bottleId) ? updated : row,
          ),
        );
      }
      setSuccessMsg(
        res.data?.message || "This appointment is now counted on that container.",
      );
      setTimeout(() => setSuccessMsg(""), 5000);
      loadMyHoldings();
    } catch (err) {
      const data = err?.response?.data;
      setUsageError(
        data?.failures?.[0]?.message ||
          data?.message ||
          "Unable to count this appointment on that container.",
      );
    } finally {
      setUsageContainerBusy(null);
    }
  };

  /**
   * OPEN A CONTAINER AND COUNT THIS JOB ON IT — in one press, from the appointment.
   *
   * `bottles/start` answers `alreadyOpen` with the existing container instead of creating a second,
   * so the two steps collapse into one path here rather than asking the professional to work out
   * which case they are in.
   */
  const openContainerAndCount = async (holding) => {
    if (!usageBooking || !holding) return;
    setUsageContainerBusy(String(holding.itemId));
    setUsageError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const started = await axios.post(
        `${baseURL}/api/service-inventory/bottles/start`,
        {
          itemId: holding.itemId,
          locationId: holding.locationId || undefined,
        },
        { headers },
      );
      const bottle = started.data?.bottle;
      if (!bottle?._id) {
        throw new Error("The container could not be opened.");
      }

      const added = await axios.post(
        `${baseURL}/api/service-inventory/bottles/${bottle._id}/appointments`,
        { appointmentRefs: [usageBooking._id] },
        { headers },
      );

      setUsageContainers((prev) => {
        const view = added.data?.bottle || bottle;
        const without = prev.filter((row) => String(row._id) !== String(view._id));
        return [view, ...without];
      });
      setSuccessMsg(
        added.data?.message ||
          started.data?.message ||
          "Container opened and this appointment counted on it.",
      );
      setTimeout(() => setSuccessMsg(""), 5000);
      loadMyHoldings();
    } catch (err) {
      const data = err?.response?.data;
      setUsageError(
        data?.failures?.[0]?.message ||
          data?.message ||
          "Unable to open that container.",
      );
    } finally {
      setUsageContainerBusy(null);
    }
  };

  const submitUsage = async () => {
    if (!usageBooking) return;
    const lines = usageHoldings
      .map((row) => ({ itemId: row.itemId, quantity: Number(usageLines[row.itemId]) }))
      .filter((line) => Number.isFinite(line.quantity) && line.quantity > 0);

    setUsageSaving(true);
    setUsageError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // One location per submit, because one endpoint call carries one. A stylist holding stock from
      // two shops is rare, and splitting the call is better than guessing which shop it came from.
      const byLocation = new Map();
      for (const line of lines) {
        const holding = usageHoldings.find((row) => row.itemId === line.itemId);
        const key = String(holding?.locationId || "");
        if (!byLocation.has(key)) byLocation.set(key, []);
        byLocation.get(key).push(line);
      }

      let saved = 0;
      const failures = [];
      for (const [locationKey, group] of byLocation) {
        // eslint-disable-next-line no-await-in-loop
        const res = await axios.post(
          `${baseURL}/api/service-inventory/consume-batch`,
          {
            locationId: locationKey || undefined,
            appointmentRefs: [usageBooking._id],
            lines: group,
          },
          { headers },
        );
        saved += res.data?.created?.length || 0;
        if (res.data?.failures?.length) {
          failures.push(...res.data.failures.map((f) => f.message));
        }
      }

      if (failures.length) {
        setUsageError(failures[0]);
      } else {
        setSuccessMsg(
          saved
            ? `Recorded ${saved} product use${saved === 1 ? "" : "s"} on this appointment.`
            : "Nothing to record.",
        );
        setTimeout(() => setSuccessMsg(""), 4000);
      }
      // The summary line above has to move with the ledger, or it becomes a number nobody trusts.
      loadMyHoldings();
      setUsageBooking(null);
    } catch (err) {
      setUsageError(
        err?.response?.data?.message || "Unable to record that usage. Please try again.",
      );
    } finally {
      setUsageSaving(false);
    }
  };

  /**
   * What this professional is holding, for the one-line summary on this page.
   *
   * Fails to empty on purpose: a professional with no staff profile and one holding nothing both
   * have nothing to say here, and an error banner about a summary line would be noise.
   */
  const loadMyHoldings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const res = await axios.get(`${baseURL}/api/service-inventory/mine`, { headers });
      setMyHoldings(res.data?.holdings || []);
    } catch {
      setMyHoldings([]);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    loadMyHoldings();
  }, [loadMyHoldings]);

  /**
   * Complete the appointment, then ask what was used.
   *
   * The asking is the point — "complete" is the one moment the stylist knows, and a link they have
   * to remember to follow afterwards is a link that is not followed. The modal can be dismissed
   * without recording anything, because a wrong record is worse than a missing one.
   */
  const completeBooking = async (booking) => {
    const customerName = booking.customerId
      ? `${booking.customerId.firstName || ""} ${booking.customerId.lastName || ""}`.trim() ||
        booking.customerName ||
        "Unknown Customer"
      : booking.customerName || "Unknown Customer";

    if (
      !window.confirm(
        `Mark this booking as completed?\n\nCustomer: ${customerName}\nService: ${booking.serviceId?.name || "Unknown Service"}\nDate: ${formatDate(booking.appointmentDate)}`,
      )
    ) {
      return;
    }

    await updateBookingStatus(booking._id, "completed");
    openUsageFor(booking);
  };

  // Request cancellation (for shop owner approval)
  const handleSubmitCancelRequest = async () => {
    if (!cancelRequestReason.trim()) {
      alert("Please provide a reason for the cancellation request.");
      return;
    }

    setCancelRequestSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(
        `${baseURL}/api/bookings/${cancelRequestBooking._id}/request-cancel`,
        { reason: cancelRequestReason },
        { headers },
      );

      setSuccessMsg("✅ Cancellation request sent to shop owner for approval.");
      setShowCancelRequestModal(false);
      setCancelRequestBooking(null);
      setCancelRequestReason("");
      fetchBookings(); // Refresh list
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (err) {
      // Better error messages for specific cases
      let errorMessage = "Unable to submit cancellation request.";
      if (err?.response?.data?.error === "duplicate_request") {
        errorMessage =
          "⚠️ A cancellation request has already been submitted for this booking.";
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setCancelRequestSubmitting(false);
    }
  };

  /**
   * OPEN A HAND-OFF: who could take this booking? The server filters to professionals who offer the service and
   * are free at that exact time, so the list cannot offer a name that would be refused.
   */
  const openHandoffModal = async (booking) => {
    setHandoffBooking(booking);
    setHandoffTargetId("");
    setHandoffNote("");
    setHandoffError("");
    setHandoffCandidates([]);
    setHandoffBusy(true);
    setShowHandoffModal(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const response = await axios.get(
        `${baseURL}/api/bookings/${booking._id}/reassign-candidates`,
        { headers },
      );
      setHandoffCandidates(
        Array.isArray(response.data?.candidates) ? response.data.candidates : [],
      );
    } catch (err) {
      setHandoffError(
        err?.response?.data?.message || "Unable to load eligible professionals.",
      );
    } finally {
      setHandoffBusy(false);
    }
  };

  const handleSubmitHandoff = async () => {
    if (!handoffTargetId) {
      setHandoffError("Choose a professional to hand this booking to.");
      return;
    }
    setHandoffBusy(true);
    setHandoffError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      await axios.post(
        `${baseURL}/api/bookings/${handoffBooking._id}/reassign-request`,
        { targetStaffId: handoffTargetId, note: handoffNote || undefined },
        { headers },
      );
      setSuccessMsg(
        "✅ Hand-off sent. The booking moves when the other professional accepts.",
      );
      setShowHandoffModal(false);
      setHandoffBooking(null);
      fetchBookings();
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (err) {
      setHandoffError(
        err?.response?.data?.message || "Unable to send the hand-off request.",
      );
    } finally {
      setHandoffBusy(false);
    }
  };

  /** Answer a hand-off that was offered to ME. Only the receiving professional can accept. */
  const respondHandoff = async (booking, accepted) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      await axios.put(
        `${baseURL}/api/bookings/${booking._id}/reassign-request/respond`,
        { accepted },
        { headers },
      );
      setSuccessMsg(
        accepted
          ? "✅ Hand-off accepted. The booking is yours."
          : "Hand-off declined. The booking stays where it is.",
      );
      fetchBookings();
      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message || "Unable to answer the hand-off request.",
      );
      setTimeout(() => setErrorMsg(""), 5000);
    }
  };

  // Mark booking as paid (cash/terminal)
  const handleMarkAsPaid = async () => {
    if (!markPaidData.amount || parseFloat(markPaidData.amount) <= 0) {
      alert("Please enter a valid payment amount.");
      return;
    }

    setMarkPaidSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.post(
        `${baseURL}/api/bookings/${markPaidBooking._id}/mark-paid`,
        {
          paymentType: markPaidData.paymentType,
          amount: parseFloat(markPaidData.amount),
          notes: markPaidData.notes,
        },
        { headers },
      );

      setSuccessMsg("✅ Booking marked as paid successfully!");
      setShowMarkPaidModal(false);
      setMarkPaidBooking(null);
      setMarkPaidData({ paymentType: "cash", amount: "", notes: "" });
      fetchBookings(); // Refresh list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      let errorMessage = "Unable to mark booking as paid.";
      if (err?.response?.data?.error === "already_paid") {
        errorMessage = "⚠️ This booking has already been marked as paid.";
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  // Open booking details modal
  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowDetailsModal(true);
  };

  // Open edit booking modal
  const openEditModal = async (booking) => {
    setSelectedBooking(booking);
    // appointmentDate is already YYYY-MM-DD string
    const bookingDate = booking.appointmentDate;

    setEditFormData({
      serviceId: booking.serviceId?._id || booking.serviceId,
      appointmentDate: bookingDate,
      startTime: booking.startTime,
      customerNotes: booking.customerNotes || "",
      paymentMethod: booking.paymentMethod || "pending",
      tipAmount: booking.tipAmount || 0,
    });

    // Ensure services are loaded
    if (services.length === 0) {
      await fetchServices();
    }

    setShowEditModal(true);

    // Fetch slots for the current date and include current booking time
    await fetchAvailableSlots(
      booking.serviceId?._id || booking.serviceId,
      bookingDate,
    );

    // Ensure current booking time is in the available slots (in case it's not showing)
    setAvailableSlots((prev) => {
      const currentTimeExists = prev.some(
        (slot) => slot.time24 === booking.startTime,
      );
      if (!currentTimeExists) {
        // Add current booking time to slots
        const [hours, minutes] = booking.startTime.split(":").map(Number);
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours >= 12 ? "PM" : "AM";
        return [
          {
            time24: booking.startTime,
            time12: `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`,
          },
          ...prev,
        ].sort((a, b) => a.time24.localeCompare(b.time24));
      }
      return prev;
    });
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.put(
        `${baseURL}/api/bookings/${selectedBooking._id}`,
        editFormData,
        { headers },
      );

      setSuccessMsg("Booking updated successfully!");
      setShowEditModal(false);
      setSelectedBooking(null);
      fetchBookings();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Unable to update booking.");
      setTimeout(() => setErrorMsg(""), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch professional's schedule
  const fetchSchedule = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/schedule`, { headers });
      setSchedule(res.data.schedule);
    } catch (err) {
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  // Fetch services for booking form
  const fetchServices = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/services`, { headers });
      const activeServices = res.data.services?.filter((s) => s.isActive) || [];
      setServices(activeServices);
    } catch (err) {
      setErrorMsg("Unable to load services.");
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  // Check if a date is available based on schedule
  const isDateAvailable = useCallback(
    (dateString) => {
      if (!schedule || !dateString) return true; // Allow if schedule not loaded yet

      const date = new Date(dateString + "T00:00:00"); // Parse as local time
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayOfWeek = dayNames[date.getDay()];

      return schedule.weeklyHours?.[dayOfWeek]?.isAvailable || false;
    },
    [schedule],
  );

  // Fetch available time slots when service and date are selected
  const fetchAvailableSlots = useCallback(
    async (serviceId, date) => {
      if (!serviceId || !date) return;

      try {
        setLoadingSlots(true);
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        // Get logged-in staff's profile to get staffId
        const profileRes = await axios.get(`${baseURL}/api/staff/profile`, {
          headers,
        });
        const staffData = profileRes.data.staff;
        const staffId = staffData?._id;

        if (!staffId) {
          setErrorMsg("Staff profile not found.");
          setAvailableSlots([]);
          return;
        }

        const res = await axios.get(
          `${baseURL}/api/bookings/available-slots?serviceId=${serviceId}&date=${date}&staffId=${staffId}`,
          { headers },
        );

        setAvailableSlots(res.data.availableSlots || []);
      } catch (err) {
        setErrorMsg("Unable to load available time slots.");
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    },
    [getAuthHeaders, getApiBaseUrl],
  );

  // Load bookings, schedule, and services on mount
  useEffect(() => {
    fetchBookings();
    fetchSchedule();
    fetchServices();
  }, [fetchBookings, fetchSchedule, fetchServices]);

  // Check for R1 → R3 booking migration on mount
  useEffect(() => {
    const checkMigration = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        const res = await axios.get(`${baseURL}/api/booking-migration/check`, {
          headers,
        });

        if (res.data.needsMigration) {
          setMigrationNeeded(true);
          setMigrationCount(res.data.r1BookingCount);
        }
      } catch (err) {
        // Silently fail - migration is optional
      }
    };

    checkMigration();
  }, [getAuthHeaders, getApiBaseUrl]);

  // Load eligible booking IDs for "Add Look" buttons (fire-and-forget, non-blocking)
  const loadEligibleBookings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return [];
      const res = await axios.get(`${baseURL}/api/looks/eligible-bookings`, {
        headers,
      });
      const eligible = res.data.eligibleBookings || [];
      const ids = new Set(eligible.map((b) => b._id));
      setLooksEligibleIds(ids);
      // Build a map so AddLookModal knows about existing looks
      const existingMap = {};
      eligible.forEach((b) => {
        if (b.existingLook) existingMap[b._id] = b.existingLook;
      });
      setLooksExistingMap(existingMap);
      setEligibleBookings(eligible);
      return eligible;
    } catch {
      // Non-fatal — silently hide the Add Look buttons if the call fails
      setLooksEligibleIds(new Set());
      setEligibleBookings([]);
      return [];
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    loadEligibleBookings();
  }, [loadEligibleBookings]);

  // Keep adding Looks one after another: after a save, open the next completed
  // booking that still has no Look.
  const handleAddAnother = useCallback(async () => {
    const eligible = await loadEligibleBookings();
    const next = eligible.find((b) => !b.existingLook) || null;
    if (!next) {
      setAddLookBooking(null);
      return;
    }
    setAddLookBooking(next);
  }, [loadEligibleBookings]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Fetch slots when both service and date are set
    if (field === "serviceId" || field === "appointmentDate") {
      const serviceId = field === "serviceId" ? value : formData.serviceId;
      const date =
        field === "appointmentDate" ? value : formData.appointmentDate;

      if (serviceId && date) {
        fetchAvailableSlots(serviceId, date);
      }
    }
  };

  // Submit new booking
  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.serviceId ||
      !formData.appointmentDate ||
      !formData.startTime
    ) {
      setErrorMsg("Please select service, date, and time slot.");
      return;
    }

    if (!formData.customerFirstName) {
      setErrorMsg("Please enter customer first name.");
      return;
    }

    try {
      setSubmitting(true);
      setErrorMsg("");

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Get staff profile to get shopId (business-centric model requires shopId)
      // This fetches the professional staff record (R3, R4, etc.) based on JWT role
      const profileRes = await axios.get(`${baseURL}/api/staff/profile`, {
        headers,
      });
      const staffData = profileRes.data.staff;
      // Use shopId directly from staff profile (it's already a string ID)
      const shopId = staffData?.shopId;
      if (!shopId) {
        setErrorMsg(
          "No location assigned to your profile. Please contact your business owner.",
        );
        return;
      }

      // A manual booking happens at the shop, so the shop/provider zone governs.
      // Never guess the customer's zone from the browser.
      const customerTimezone = professionalTimezone;

      const bookingData = {
        ...formData,
        shopId, // Add shopId for business-centric model
        customerTimezone,
      };
      await axios.post(`${baseURL}/api/bookings/staff`, bookingData, {
        headers,
      });

      setSuccessMsg("Booking created successfully!");
      setShowNewBookingForm(false);

      // Reset form
      setFormData({
        serviceId: "",
        bookingType: "walk-in",
        customerFirstName: "",
        customerLastName: "",
        customerPhone: "",
        customerEmail: "",
        appointmentDate: "",
        startTime: "",
        customerNotes: "",
      });
      setAvailableSlots([]);

      fetchBookings(); // Refresh list
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          "Unable to create booking. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle R1 → R3 booking migration
  const handleMigrateBookings = async () => {
    try {
      setMigrationLoading(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.post(
        `${baseURL}/api/booking-migration/migrate`,
        {},
        { headers },
      );

      setSuccessMsg(res.data.message || "Bookings migrated successfully!");
      setMigrationNeeded(false);
      setShowMigrationModal(false);

      // Refresh bookings list
      fetchBookings();
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Failed to migrate bookings");
    } finally {
      setMigrationLoading(false);
    }
  };

  // Open form and load services
  const openBookingForm = () => {
    setShowNewBookingForm(true);
    if (services.length === 0) {
      fetchServices();
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    const colors = {
      pending: { bg: "#fff3cd", border: "#ffc107", text: "#856404" },
      confirmed: { bg: "#d1ecf1", border: "#17a2b8", text: "#0c5460" },
      completed: { bg: "#d4edda", border: "#28a745", text: "#155724" },
      cancelled: { bg: "#f8d7da", border: "#dc3545", text: "#721c24" },
      "no-show": { bg: "#e2e3e5", border: "#6c757d", text: "#383d41" },
      // Part B: the customer paid, but their slot was taken mid-payment and no
      // replacement time was free. Needs a human, so it gets the alert colour.
      slot_conflict: { bg: "#fef3c7", border: "#f59e0b", text: "#92400e" },
    };
    return colors[status] || colors.pending;
  };

  // Filter bookings by status
  const filteredBookings = bookings.filter((booking) => {
    // "today" tab: show ALL of today's bookings regardless of status so the
    // professional always sees every booking scheduled for the day.
    if (filterStatus === "today") {
      const todayStr = getTodayStringInProfessionalTimezone();
      return booking.appointmentDate === todayStr;
    }

    // "upcoming" tab: confirmed bookings from tomorrow onward. A parked slot
    // conflict is included deliberately - it is paid work that still needs this
    // professional to reach out to the customer, so hiding it would bury it.
    if (filterStatus === "upcoming") {
      const todayStr = getTodayStringInProfessionalTimezone();
      return (
        (booking.status === "confirmed" ||
          booking.status === "slot_conflict") &&
        booking.appointmentDate > todayStr
      );
    }

    // Status filter for other tabs
    if (filterStatus !== "all" && booking.status !== filterStatus) return false;

    return true;
  });

  // Group bookings by date
  const groupedBookings = filteredBookings.reduce((groups, booking) => {
    const date = booking.appointmentDate; // Already YYYY-MM-DD string
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(booking);
    return groups;
  }, {});

  if (loading) {
    return (
      <StaffSidebarLayout roleType={getRoleType()}>
        <div className="loading">Loading bookings...</div>
      </StaffSidebarLayout>
    );
  }

  return (
    <StaffSidebarLayout roleType={getRoleType()}>
      <div
        style={{
          padding: isMobile ? "12px" : "24px",
          maxWidth: "100%",
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            marginBottom: "24px",
            gap: isMobile ? "12px" : "16px",
          }}
        >
          <button
            onClick={() => navigate(`/dashboard/${getRoleType()}`)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 18px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              background: "#fff",
              color: "#333",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "center" : "flex-start",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#f5f5f5";
              e.target.style.borderColor = "#001d4a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#fff";
              e.target.style.borderColor = "#ddd";
            }}
          >
            <span>←</span> Back to Dashboard
          </button>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flex: 1,
              justifyContent: "flex-end",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              onClick={() => navigate(`/dashboard/${getRoleType()}/bookings/import`)}
              style={{
                padding: "10px 20px",
                background: "#fff",
                color: "#2d6da3",
                border: "1px solid #2d6da3",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              📥 Import Appointments
            </button>
            {/* Walk-in (Phase 1): intentionally OUTSIDE the
                PROVIDER_SELF_BOOKING_ENABLED gate. That flag records a different
                decision (no provider self-booking) and must stay false; a walk-in
                is a customer present at the desk, settled now. */}
            <WalkInButton label="Walk-in" />
            {PROVIDER_SELF_BOOKING_ENABLED && (
              <button
                onClick={openBookingForm}
                style={{
                  padding: "10px 24px",
                  background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(45, 109, 163, 0.3)",
                  transition: "all 0.2s",
                  width: isMobile ? "100%" : "auto",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow =
                    "0 6px 16px rgba(45, 109, 163, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 4px 12px rgba(45, 109, 163, 0.3)";
                }}
              >
                + Add New Booking
              </button>
            )}
          </div>
        </div>

        {/* Page Title and Stats */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #2d6da3 100%)",
            borderRadius: "16px",
            padding: isMobile ? "24px" : "32px",
            marginBottom: "24px",
            color: "white",
            boxShadow: "0 8px 24px rgba(45, 109, 163, 0.2)",
          }}
        >
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: isMobile ? "24px" : "32px",
              fontWeight: "700",
            }}
          >
            📅 My Bookings
          </h2>
          <p
            style={{
              margin: 0,
              fontSize: isMobile ? "14px" : "16px",
              opacity: 0.9,
            }}
          >
            Manage your appointments and schedule
          </p>
        </div>

        {/*
          WHAT IS IN YOUR HANDS, ON THE SCREEN YOU ARE ALREADY ON.

          The other half of recording use at the appointment: a stylist has to be able to SEE what
          they are holding without going to look for it, or the amount they type is a guess and the
          stock quietly stops agreeing with the shelf. Kept to one line — it is a reminder, not a
          report, and the detail lives on Service Inventory.
        */}
        {myHoldings.length > 0 && (
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "20px",
              fontSize: "13px",
              color: "#334155",
            }}
          >
            <strong style={{ color: "#0f172a" }}>You are holding:</strong>{" "}
            {myHoldings
              .map(
                (row) =>
                  `${row.name} ${row.outstanding}${
                    row.unit === "each" ? "" : ` ${row.unit}`
                  }`,
              )
              .join(" · ")}
            <div style={{ marginTop: "4px", color: "#64748b" }}>
              Record what you use from the appointment you used it on — the button is on the
              booking below.
            </div>
          </div>
        )}

        {errorMsg && (
          <div
            style={{
              padding: "16px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              borderRadius: "8px",
              color: "#991b1b",
              marginBottom: "24px",
            }}
          >
            {errorMsg}
          </div>
        )}

        {successMsg && (
          <div
            style={{
              padding: "16px",
              background: "#d1fae5",
              border: "1px solid #10b981",
              borderRadius: "8px",
              color: "#065f46",
              marginBottom: "24px",
            }}
          >
            {successMsg}
          </div>
        )}

        {/* HAND-OFFS OFFERED TO ME (2026-10-24). The receiving professional must accept, so the request has to be
            visible where they work. Declining leaves the booking exactly where it is. */}
        {incomingHandoffs.length > 0 && (
          <div
            style={{
              padding: "16px",
              background: "#eef2ff",
              border: "1px solid #c7d2fe",
              borderRadius: "8px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                fontWeight: "700",
                color: "#3730a3",
                marginBottom: "10px",
              }}
            >
              🔄 {incomingHandoffs.length} booking
              {incomingHandoffs.length === 1 ? "" : "s"} offered to you
            </div>
            {incomingHandoffs.map((request) => (
              <div
                key={request._id}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 0",
                  borderTop: "1px solid #e0e7ff",
                  fontSize: "13px",
                  color: "#312e81",
                }}
              >
                <span style={{ flex: "1 1 260px" }}>
                  <strong>{request.serviceName}</strong>
                  {request.customerName ? ` · ${request.customerName}` : ""} ·{" "}
                  {request.appointmentDate} at {request.startTime}
                  {request.shopName ? ` · ${request.shopName}` : ""}
                  {request.note ? ` — “${request.note}”` : ""}
                </span>
                <button
                  onClick={() => respondHandoff(request, true)}
                  style={{
                    padding: "6px 14px",
                    background: "#4338ca",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Accept
                </button>
                <button
                  onClick={() => respondHandoff(request, false)}
                  style={{
                    padding: "6px 14px",
                    background: "white",
                    color: "#3730a3",
                    border: "1px solid #c7d2fe",
                    borderRadius: "6px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Decline
                </button>
              </div>
            ))}
          </div>
        )}

        {/* R1 → R3 Booking Migration Banner (Option B) */}
        {migrationNeeded && (
          <div
            style={{
              padding: "20px",
              background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
              border: "2px solid #f59e0b",
              borderRadius: "12px",
              marginBottom: "24px",
              boxShadow: "0 4px 12px rgba(245, 158, 11, 0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              <div style={{ fontSize: "32px" }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "18px",
                    fontWeight: "700",
                    color: "#92400e",
                  }}
                >
                  Bookings Found on Owner Profile
                </h3>
                <p
                  style={{
                    margin: "0 0 16px 0",
                    fontSize: "14px",
                    color: "#78350f",
                    lineHeight: "1.6",
                  }}
                >
                  You have <strong>{migrationCount} bookings</strong> assigned
                  to your Owner (R1) role instead of your Professional (R3)
                  role. These bookings won't appear in your professional
                  dashboard until migrated.
                  <br />
                  <br />
                  Click below to move them to your Professional profile so they
                  appear in this dashboard.
                </p>
                <button
                  onClick={() => setShowMigrationModal(true)}
                  disabled={migrationLoading}
                  style={{
                    padding: "10px 20px",
                    background: migrationLoading
                      ? "#d97706"
                      : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: migrationLoading ? "not-allowed" : "pointer",
                    opacity: migrationLoading ? 0.7 : 1,
                    transition: "all 0.2s",
                  }}
                >
                  {migrationLoading
                    ? "Migrating..."
                    : `Migrate ${migrationCount} Bookings`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Filter Pills */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            padding: isMobile ? "0" : "0 4px",
          }}
        >
          {[
            { value: "today", label: "Today", icon: "📅" },
            { value: "upcoming", label: "Upcoming", icon: "🗓️" },
            { value: "confirmed", label: "Confirmed", icon: "✅" },
            { value: "completed", label: "Completed", icon: "🎉" },
            { value: "cancelled", label: "Cancelled", icon: "❌" },
            { value: "no-show", label: "No Show", icon: "⊘" },
            { value: "all", label: "All", icon: "📋" },
          ].map((status) => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              style={{
                padding: "10px 20px",
                background:
                  filterStatus === status.value
                    ? "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)"
                    : "#fff",
                color: filterStatus === status.value ? "#fff" : "#6b7280",
                border:
                  filterStatus === status.value
                    ? "2px solid #2d6da3"
                    : "2px solid #e5e7eb",
                borderRadius: "24px",
                fontSize: "14px",
                fontWeight: filterStatus === status.value ? "600" : "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow:
                  filterStatus === status.value
                    ? "0 4px 12px rgba(45, 109, 163, 0.2)"
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (filterStatus !== status.value) {
                  e.target.style.borderColor = "#2d6da3";
                  e.target.style.background = "#f0f9ff";
                }
              }}
              onMouseLeave={(e) => {
                if (filterStatus !== status.value) {
                  e.target.style.borderColor = "#e5e7eb";
                  e.target.style.background = "#fff";
                }
              }}
            >
              <span style={{ marginRight: "6px" }}>{status.icon}</span>
              {status.label}
            </button>
          ))}
        </div>

        {/* Bookings List */}
        {Object.keys(groupedBookings).length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: isMobile ? "48px 20px" : "80px 20px",
              background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
              borderRadius: "16px",
              border: "2px dashed #d1d5db",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "64px" : "80px",
                marginBottom: "16px",
              }}
            >
              📅
            </div>
            <h3
              style={{
                color: "#374151",
                fontSize: isMobile ? "20px" : "24px",
                marginBottom: "12px",
                fontWeight: "600",
              }}
            >
              No bookings found
            </h3>
            <p
              style={{
                color: "#6b7280",
                fontSize: isMobile ? "15px" : "16px",
                marginBottom: "24px",
              }}
            >
              {filterStatus === "today"
                ? "You have no more appointments for today"
                : filterStatus === "upcoming"
                  ? "You have no upcoming appointments"
                  : "No bookings match your filters"}
            </p>
            {PROVIDER_SELF_BOOKING_ENABLED && (
              <button
                onClick={openBookingForm}
                style={{
                  padding: "12px 32px",
                  background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(45, 109, 163, 0.3)",
                }}
              >
                Create First Booking
              </button>
            )}
          </div>
        ) : (
          Object.keys(groupedBookings)
            .sort((a, b) => new Date(a) - new Date(b))
            .map((date) => (
              <div key={date} style={{ marginBottom: "32px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "16px",
                    paddingBottom: "12px",
                    borderBottom: "3px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      width: "4px",
                      height: "24px",
                      background:
                        "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      borderRadius: "2px",
                    }}
                  ></div>
                  <h3
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      color: "#111",
                      margin: 0,
                      fontWeight: "700",
                    }}
                  >
                    {formatDate(date)}
                  </h3>
                  <span
                    style={{
                      marginLeft: "auto",
                      padding: "4px 12px",
                      background: "#f0f9ff",
                      color: "#2d6da3",
                      borderRadius: "12px",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    {groupedBookings[date].length} booking
                    {groupedBookings[date].length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Bookings Grid */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(380px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {groupedBookings[date]
                    .slice()
                    .sort((a, b) =>
                      (a.startTime || "").localeCompare(b.startTime || ""),
                    )
                    .map((booking) => {
                      const statusColor = getStatusColor(booking.status);
                      const customerName = booking.customerId
                        ? `${booking.customerId.firstName} ${booking.customerId.lastName}`
                        : booking.customerName;

                      return (
                        <div
                          key={booking._id}
                          style={{
                            background: "#fff",
                            border: "2px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "20px",
                            transition: "all 0.2s ease",
                            cursor: "default",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#2d6da3";
                            e.currentTarget.style.boxShadow =
                              "0 8px 24px rgba(45, 109, 163, 0.15)";
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#e5e7eb";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          {/* Header */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "16px",
                              gap: "12px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <h4
                                  style={{
                                    fontSize: "17px",
                                    fontWeight: "700",
                                    margin: 0,
                                    color: "#111",
                                  }}
                                >
                                  {booking.serviceId?.name}
                                </h4>
                                <TestBadge isTest={booking.isTestBooking} />
                                {booking.isRecurring && (
                                  <span
                                    style={{
                                      padding: "3px 10px",
                                      background: "#dbeafe",
                                      color: "#1e40af",
                                      border: "1px solid #3b82f6",
                                      borderRadius: "12px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    🔄 Recurring
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "6px 14px",
                                borderRadius: "12px",
                                fontSize: "12px",
                                fontWeight: "700",
                                background:
                                  booking.cancellationRequest?.status ===
                                    "pending" &&
                                  booking.cancellationRequest?.reason
                                    ? "#fef3c7"
                                    : statusColor.bg,
                                border: `2px solid ${
                                  booking.cancellationRequest?.status ===
                                    "pending" &&
                                  booking.cancellationRequest?.reason
                                    ? "#fbbf24"
                                    : statusColor.border
                                }`,
                                color:
                                  booking.cancellationRequest?.status ===
                                    "pending" &&
                                  booking.cancellationRequest?.reason
                                    ? "#92400e"
                                    : statusColor.text,
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {booking.cancellationRequest?.status ===
                                "pending" && booking.cancellationRequest?.reason
                                ? "CANCEL PENDING"
                                : booking.status === "slot_conflict"
                                  ? "TIME NEEDS CONFIRMING"
                                  : booking.status}
                            </div>
                          </div>

                          {/* Part B: paid but not seated. Tell the professional what
                              happened and what is expected of them. */}
                          {booking.status === "slot_conflict" && (
                            <div
                              style={{
                                padding: "12px",
                                background: "#fffbeb",
                                border: "1px solid #f59e0b",
                                borderRadius: "8px",
                                marginBottom: "12px",
                              }}
                            >
                              <strong
                                style={{
                                  display: "block",
                                  fontSize: "13px",
                                  color: "#92400e",
                                  marginBottom: "4px",
                                }}
                              >
                                ⚠️ Paid, but no slot held
                              </strong>
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "13px",
                                  color: "#78350f",
                                  lineHeight: 1.5,
                                }}
                              >
                                {customerName || "The customer"}&apos;s slot was
                                booked by someone else while they were paying, and
                                we could not find another time. Their payment is
                                being held - please contact them to agree a time.
                                {booking.slotConflict?.autoRefundAt
                                  ? ` If it is not resolved by ${new Date(
                                      booking.slotConflict.autoRefundAt,
                                    ).toLocaleDateString("en-US", {
                                      month: "long",
                                      day: "numeric",
                                    })}, the payment is refunded automatically.`
                                  : " If it is not resolved, the payment is refunded automatically."}
                              </p>
                            </div>
                          )}

                          {/* Customer Info */}
                          <div
                            style={{
                              padding: "12px",
                              background: "#f9fafb",
                              borderRadius: "8px",
                              marginBottom: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "6px",
                              }}
                            >
                              <span style={{ fontSize: "16px" }}>👤</span>
                              <p
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: "#374151",
                                  margin: 0,
                                }}
                              >
                                {customerName}
                              </p>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              {/* No icon span here. renderTimeWithTimezone()
                                  already prefixes the time with a clock, so this
                                  span rendered a SECOND one — two clocks side by
                                  side on the card. */}
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#6b7280",
                                  fontWeight: "500",
                                }}
                              >
                                {renderTimeWithTimezone(booking)}
                              </div>
                            </div>
                            {booking.customerPhone && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginTop: "6px",
                                }}
                              >
                                <span style={{ fontSize: "16px" }}>📞</span>
                                <p
                                  style={{
                                    fontSize: "14px",
                                    color: "#6b7280",
                                    margin: 0,
                                    fontWeight: "500",
                                  }}
                                >
                                  {booking.customerPhone}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Customer Notes */}
                          {booking.customerNotes && (
                            <div
                              style={{
                                padding: "12px",
                                background: "#fffbeb",
                                border: "1px solid #fde68a",
                                borderRadius: "8px",
                                marginBottom: "12px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#92400e",
                                  margin: 0,
                                  lineHeight: "1.5",
                                }}
                              >
                                <strong style={{ fontWeight: "600" }}>
                                  📝 Note:
                                </strong>{" "}
                                {booking.customerNotes}
                              </p>
                            </div>
                          )}

                          {/* Historical Payment Badge */}
                          {booking.paidInExternalSystem && (
                            <div
                              style={{
                                padding: "8px 12px",
                                background: "#f3f4f6",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                marginBottom: "12px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  margin: 0,
                                  fontWeight: "600",
                                }}
                              >
                                🏛️ Historical Appointment
                              </p>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(110px, 1fr))",
                              gap: "8px",
                              marginTop: "12px",
                              paddingTop: "12px",
                              borderTop: "1px solid #e5e7eb",
                            }}
                          >
                            {/* View Details - Always available */}
                            <button
                              onClick={() => viewBookingDetails(booking)}
                              style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                background: "#f0f9ff",
                                color: "#1e40af",
                                border: "1px solid #bfdbfe",
                                borderRadius: "6px",
                                cursor: "pointer",
                                fontWeight: "600",
                                transition: "all 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#dbeafe";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "#f0f9ff";
                              }}
                            >
                              👁️ View
                            </button>

                            {/* Cancel walk-in — ONLY for a walk-in still awaiting
                                payment. Deliberately NOT shown for online pending
                                bookings: those must go through the normal
                                cancellation flow, not a slot release. */}
                            {booking.isWalkIn &&
                              booking.status === "pending_payment" && (
                                <button
                                  onClick={async () => {
                                    if (
                                      !window.confirm(
                                        "Cancel this walk-in? The held time slot will be released immediately.",
                                      )
                                    ) {
                                      return;
                                    }
                                    try {
                                      const base = getApiBaseUrl();
                                      await axios.post(
                                        `${base}/api/bookings/walk-in/${booking._id}/cancel`,
                                        {},
                                        { headers: getAuthHeaders() },
                                      );
                                      await fetchBookings();
                                    } catch (err) {
                                      setErrorMsg(
                                        err?.response?.data?.message ||
                                          "Unable to cancel this walk-in.",
                                      );
                                    }
                                  }}
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    background: "#fee2e2",
                                    color: "#991b1b",
                                    border: "1px solid #fecaca",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = "#fecaca";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = "#fee2e2";
                                  }}
                                >
                                  ✖ Cancel walk-in
                                </button>
                              )}

                            {/* Edit - Only for pending/confirmed.
                                P0a (F3): hidden while provider self-booking is
                                paused, because this path writes service/date
                                without the pricing engine (P10 restores it). */}
                            {PROVIDER_SELF_BOOKING_ENABLED &&
                              (booking.status === "pending" ||
                                booking.status === "confirmed") &&
                              !(
                                booking.cancellationRequest?.status ===
                                  "pending" &&
                                booking.cancellationRequest?.reason
                              ) && (
                                <button
                                  onClick={() => openEditModal(booking)}
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    background: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde68a",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "600",
                                    transition: "all 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = "#fde68a";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = "#fef3c7";
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                              )}

                            {booking.status !== "completed" &&
                              booking.status !== "cancelled" && (
                                <>
                                  {booking.status === "pending" && (
                                    <button
                                      onClick={() =>
                                        updateBookingStatus(
                                          booking._id,
                                          "confirmed",
                                        )
                                      }
                                      style={{
                                        padding: "8px 12px",
                                        fontSize: "13px",
                                        background: "#d1fae5",
                                        color: "#065f46",
                                        border: "1px solid #6ee7b7",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontWeight: "600",
                                        transition: "all 0.2s",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.background = "#a7f3d0";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.background = "#d1fae5";
                                      }}
                                    >
                                      ✓ Confirm
                                    </button>
                                  )}
                                  {(booking.status === "pending" ||
                                    booking.status === "confirmed") && (
                                    <>
                                      {!(
                                        booking.cancellationRequest?.status ===
                                          "pending" &&
                                        booking.cancellationRequest?.reason
                                      ) && (
                                        <button
                                          onClick={() => completeBooking(booking)}
                                          style={{
                                            padding: "8px 12px",
                                            fontSize: "13px",
                                            background: "#cffafe",
                                            color: "#155e75",
                                            border: "1px solid #67e8f9",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontWeight: "600",
                                            transition: "all 0.2s",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.background =
                                              "#a5f3fc";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.background =
                                              "#cffafe";
                                          }}
                                        >
                                          ✓ Complete
                                        </button>
                                      )}

                                      {/* Mark as Paid button - ONLY for non-Stripe bookings (walk-in, phone, manual) */}
                                      {/* Security: Prevents double-payment fraud (Stripe payout + marking as cash) */}
                                      {booking.status === "completed" &&
                                        !booking.inShopPayment?.isPaid &&
                                        !booking.stripePaymentIntentId && (
                                          <button
                                            onClick={() => {
                                              setMarkPaidBooking(booking);
                                              // Pre-fill amount with service price + tip if available
                                              const totalAmount =
                                                (booking.servicePriceAtBooking ||
                                                  0) +
                                                (booking.tipAmount || 0) +
                                                (booking.productsTotalPrice ||
                                                  0);
                                              setMarkPaidData({
                                                paymentType: "cash",
                                                amount: totalAmount.toFixed(2),
                                                notes: "",
                                              });
                                              setShowMarkPaidModal(true);
                                            }}
                                            style={{
                                              padding: "6px 12px",
                                              fontSize: "14px",
                                              background: "#10b981",
                                              color: "white",
                                              border: "none",
                                              borderRadius: "6px",
                                              cursor: "pointer",
                                              fontWeight: "500",
                                            }}
                                          >
                                            💵 Mark as Paid
                                          </button>
                                        )}

                                      {/* Show paid status if already marked */}
                                      {booking.inShopPayment?.isPaid && (
                                        <div
                                          style={{
                                            padding: "6px 12px",
                                            fontSize: "12px",
                                            background: "#d1fae5",
                                            color: "#065f46",
                                            border: "1px solid #10b981",
                                            borderRadius: "6px",
                                            fontWeight: "600",
                                            display: "inline-block",
                                          }}
                                        >
                                          ✓ Paid (
                                          {booking.inShopPayment.paymentType})
                                        </div>
                                      )}

                                      {booking.cancellationRequest?.status ===
                                        "pending" &&
                                      booking.cancellationRequest?.reason ? (
                                        <div
                                          style={{
                                            padding: "8px 12px",
                                            fontSize: "13px",
                                            background: "#fef3c7",
                                            color: "#92400e",
                                            border: "1px solid #fbbf24",
                                            borderRadius: "6px",
                                            fontWeight: "600",
                                            display: "inline-block",
                                            textAlign: "center",
                                            gridColumn: "span 2",
                                          }}
                                        >
                                          ⏳ Cancel Pending
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => {
                                            setCancelRequestBooking(booking);
                                            setShowCancelRequestModal(true);
                                          }}
                                          style={{
                                            padding: "8px 12px",
                                            fontSize: "13px",
                                            background: "#fef3c7",
                                            color: "#92400e",
                                            border: "1px solid #fde68a",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontWeight: "600",
                                            transition: "all 0.2s",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.background =
                                              "#fde68a";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.background =
                                              "#fef3c7";
                                          }}
                                        >
                                          📋 Cancel
                                        </button>
                                      )}
                                      {/* HAND OFF (2026-10-24): ask a colleague to take this booking. It only
                                          moves when they accept — a professional cannot silently reassign. */}
                                      {["pending", "confirmed"].includes(
                                        booking.status,
                                      ) &&
                                        !(
                                          booking.reassignmentRequest?.status ===
                                          "pending"
                                        ) && (
                                          <button
                                            onClick={() =>
                                              openHandoffModal(booking)
                                            }
                                            style={{
                                              padding: "8px 12px",
                                              fontSize: "13px",
                                              background: "#eef2ff",
                                              color: "#3730a3",
                                              border: "1px solid #c7d2fe",
                                              borderRadius: "6px",
                                              cursor: "pointer",
                                              fontWeight: "600",
                                            }}
                                          >
                                            🔄 Hand off
                                          </button>
                                        )}
                                      {booking.reassignmentRequest?.status ===
                                        "pending" && (
                                        <div
                                          style={{
                                            padding: "8px 12px",
                                            fontSize: "12px",
                                            background: "#eef2ff",
                                            color: "#3730a3",
                                            border: "1px solid #c7d2fe",
                                            borderRadius: "6px",
                                            fontWeight: "600",
                                            display: "inline-block",
                                            textAlign: "center",
                                          }}
                                        >
                                          ⏳ Hand-off pending
                                        </div>
                                      )}
                                      {!(
                                        booking.cancellationRequest?.status ===
                                          "pending" &&
                                        booking.cancellationRequest?.reason
                                      ) && (
                                        <button
                                          onClick={() => {
                                            const customerName =
                                              booking.customerId
                                                ? `${booking.customerId.firstName || ""} ${booking.customerId.lastName || ""}`.trim() ||
                                                  booking.customerName ||
                                                  "Unknown Customer"
                                                : booking.customerName ||
                                                  "Unknown Customer";
                                            if (
                                              window.confirm(
                                                `Mark this booking as no-show?\n\nCustomer: ${customerName}\nService: ${booking.serviceId?.name || "Unknown Service"}\nDate: ${formatDate(booking.appointmentDate)}`,
                                              )
                                            ) {
                                              updateBookingStatus(
                                                booking._id,
                                                "no-show",
                                              );
                                            }
                                          }}
                                          style={{
                                            padding: "8px 12px",
                                            fontSize: "13px",
                                            background: "#f3f4f6",
                                            color: "#4b5563",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontWeight: "600",
                                            transition: "all 0.2s",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.target.style.background =
                                              "#e5e7eb";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.target.style.background =
                                              "#f3f4f6";
                                          }}
                                        >
                                          ⊘ No-Show
                                        </button>
                                      )}
                                    </>
                                  )}
                                </>
                              )}

                            {/*
                              RECORD WHAT WAS USED, right where the appointment is.

                              COMPLETED ONLY. Product is recorded as used once the service has been
                              delivered; a `confirmed` booking is usually still ahead of them, and
                              offering the field there invites an amount recorded in advance — which
                              the ledger cannot tell apart from one recorded afterwards.

                              And only for the role that delivered it (see canRecordProductUsed):
                              the desk issues stock to a named person and does not record what that
                              person used.
                            */}
                            {booking.status === "completed" && canRecordProductUsed() && (
                              <button
                                onClick={() => openUsageFor(booking)}
                                style={{
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  background: "#eef2ff",
                                  color: "#3730a3",
                                  border: "1px solid #c7d2fe",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#e0e7ff";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#eef2ff";
                                }}
                              >
                                🧴 Record product used
                              </button>
                            )}

                            {/* Add Look button — available on completed bookings */}
                            {booking.status === "completed" && (
                              <button
                                onClick={() => setAddLookBooking(booking)}
                                style={{
                                  padding: "8px 12px",
                                  fontSize: "13px",
                                  background: "#f0fdf4",
                                  color: "#15803d",
                                  border: "1px solid #86efac",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontWeight: "600",
                                  transition: "all 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#dcfce7";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#f0fdf4";
                                }}
                              >
                                {looksExistingMap[booking._id]?.imageCount >= 4
                                  ? "📸 Manage Look"
                                  : looksExistingMap[booking._id]
                                    ? "📸 Add Another Look"
                                    : "📸 Add Look"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))
        )}

        {/* Add Look Modal (shared component) */}
        {addLookBooking && (
          <AddLookModal
            key={addLookBooking._id}
            booking={addLookBooking}
            existingLook={
              addLookBooking
                ? looksExistingMap[addLookBooking._id] || null
                : null
            }
            getAuthHeaders={getAuthHeaders}
            getApiBaseUrl={getApiBaseUrl}
            onClose={() => setAddLookBooking(null)}
            onAddAnother={handleAddAnother}
            onSuccess={(bookingId) => {
              setLooksEligibleIds((prev) => {
                const next = new Set(prev);
                next.delete(bookingId);
                return next;
              });
            }}
          />
        )}

        {/* Record product used modal */}
        {usageBooking && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15,23,42,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "16px",
              zIndex: 1200,
            }}
            onClick={() => !usageSaving && setUsageBooking(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "white",
                borderRadius: "12px",
                width: "100%",
                maxWidth: "560px",
                maxHeight: "85vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
              }}
            >
              <div style={{ padding: "18px 20px", borderBottom: "1px solid #e5e7eb" }}>
                <h2 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>
                  What did you use?
                </h2>
                <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#64748b" }}>
                  {usageBooking.serviceId?.name || "This service"} —{" "}
                  {usageBooking.customerId
                    ? `${usageBooking.customerId.firstName || ""} ${usageBooking.customerId.lastName || ""}`.trim()
                    : usageBooking.customerName || "Customer"}{" "}
                  — {formatDate(usageBooking.appointmentDate)}
                </p>
              </div>

              <div style={{ padding: "18px 20px", overflowY: "auto" }}>
                {usageError ? (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      color: "#991b1b",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      marginBottom: "14px",
                    }}
                  >
                    {usageError}
                  </div>
                ) : null}

                {usageLoading ? (
                  <p style={{ color: "#64748b", fontSize: "14px" }}>Loading your products…</p>
                ) : (
                  <>
                    {/*
                      WHAT IS ALREADY ON THIS APPOINTMENT.

                      Shown ABOVE the fields rather than blocking them: `consume-batch` has no
                      already-recorded guard, so without this the second press of Save silently
                      doubled the product cost of one client. A correction is still possible - the
                      reader can see the figure they are adding to.
                    */}
                    {usageRecorded.length > 0 ? (
                      <div
                        style={{
                          background: "#fffbeb",
                          border: "1px solid #fde68a",
                          borderRadius: "8px",
                          padding: "12px 14px",
                          fontSize: "13px",
                          color: "#92400e",
                          marginBottom: "14px",
                        }}
                      >
                        <strong>Already recorded on this appointment</strong>
                        <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>
                          {usageRecorded.map((row) => (
                            <li key={row.movementId}>
                              {row.itemName} — {row.quantity} {row.unit === "each" ? "" : row.unit}
                            </li>
                          ))}
                        </ul>
                        <p style={{ margin: "6px 0 0" }}>
                          Anything you add here is added on top of it.
                        </p>
                      </div>
                    ) : null}

                    {usageHoldings.length === 0 &&
                    usageSharedHoldings.length === 0 &&
                    usageContainers.length === 0 ? (
                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px dashed #cbd5e1",
                          borderRadius: "8px",
                          padding: "16px",
                          fontSize: "14px",
                          color: "#475569",
                        }}
                      >
                        <strong>Nothing is in your hands to record.</strong>
                        <p style={{ margin: "8px 0 0" }}>
                          Product you use has to be given to you first — the desk does that from
                          Service Inventory. Once it is yours, it shows up here.
                        </p>
                      </div>
                    ) : null}

                    {usageHoldings.length > 0 ? (
                      <>
                        <p style={{ margin: "0 0 14px", fontSize: "13px", color: "#64748b" }}>
                          Only what you are holding. Leave anything you did not use empty.
                        </p>
                        {usageHoldings.map((row) => (
                          <div
                            key={row.itemId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "10px 0",
                              borderBottom: "1px solid #f1f5f9",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}>
                                {row.name}
                              </div>
                              <div style={{ fontSize: "12px", color: "#64748b" }}>
                                You hold {row.outstanding} {row.unit === "each" ? "" : row.unit}
                                {usagePrefilled.includes(row.itemId) ? " · last time's amount" : ""}
                              </div>
                            </div>
                            <input
                              type="number"
                              min="0"
                              step="any"
                              value={usageLines[row.itemId] ?? ""}
                              placeholder="0"
                              onChange={(e) =>
                                setUsageLines((prev) => ({
                                  ...prev,
                                  [row.itemId]: e.target.value,
                                }))
                              }
                              style={{
                                width: "96px",
                                padding: "8px 10px",
                                fontSize: "14px",
                                border: "1px solid #cbd5e1",
                                borderRadius: "6px",
                                textAlign: "right",
                              }}
                            />
                            <span style={{ fontSize: "12px", color: "#64748b", width: "34px" }}>
                              {row.unit === "each" ? "each" : row.unit}
                            </span>
                          </div>
                        ))}
                      </>
                    ) : null}

                    {/*
                      SHARED PRODUCTS — counted, not measured.

                      A container's cost is divided by how many jobs it covers, so the only true
                      statement about this appointment is "yes, this client was one of them". The
                      amount fields above cannot express that: a quantity here would charge this one
                      client for a container that served several, which `consume-batch` refuses.
                    */}
                    {usageContainers.length > 0 || usageSharedHoldings.length > 0 ? (
                      <div style={{ marginTop: "18px" }}>
                        <h3
                          style={{
                            margin: "0 0 4px",
                            fontSize: "14px",
                            color: "#0f172a",
                          }}
                        >
                          Shared products
                        </h3>
                        <p style={{ margin: "0 0 10px", fontSize: "12px", color: "#64748b" }}>
                          A container is divided between every job it covers, so being on the list
                          is the record. Nothing is charged to this client alone.
                        </p>

                        {usageContainers.map((bottle) => {
                          const covered = (bottle.appointments || []).some(
                            (row) => String(row.bookingId) === String(usageBooking?._id),
                          );
                          const busy = usageContainerBusy === String(bottle._id);
                          return (
                            <div
                              key={bottle._id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "10px 0",
                                borderBottom: "1px solid #f1f5f9",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}
                                >
                                  {bottle.name}
                                </div>
                                <div style={{ fontSize: "12px", color: "#64748b" }}>
                                  Covers {bottle.coveredCount || 0} job
                                  {bottle.coveredCount === 1 ? "" : "s"}
                                  {bottle.seesCost && bottle.shareCost
                                    ? ` · ${bottle.shareCost.toFixed(2)} each once finished`
                                    : ""}
                                  {bottle.locationName ? ` · ${bottle.locationName}` : ""}
                                </div>
                              </div>
                              {covered ? (
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#15803d",
                                    fontWeight: 600,
                                  }}
                                >
                                  ✓ Counted on this job
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => countBookingOnContainer(bottle._id)}
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    background: "#eef2ff",
                                    color: "#3730a3",
                                    border: "1px solid #c7d2fe",
                                    borderRadius: "6px",
                                    cursor: busy ? "default" : "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  {busy ? "Adding…" : "Count this job"}
                                </button>
                              )}
                            </div>
                          );
                        })}

                        {/*
                          HOLDING A SHARED PRODUCT WITH NOTHING OPEN — the case that used to be a
                          dead end. Opening the container from here is the same act as opening it in
                          Service Inventory, done where the answer is known.
                        */}
                        {usageSharedHoldings
                          .filter(
                            (holding) =>
                              !usageContainers.some(
                                (bottle) => String(bottle.itemId) === String(holding.itemId),
                              ),
                          )
                          .map((holding) => {
                            const busy = usageContainerBusy === String(holding.itemId);
                            return (
                              <div
                                key={holding.itemId}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  padding: "10px 0",
                                  borderBottom: "1px solid #f1f5f9",
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div
                                    style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}
                                  >
                                    {holding.name}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#64748b" }}>
                                    You hold {holding.outstanding}{" "}
                                    {holding.unit === "each" ? "" : holding.unit} — no container
                                    open yet
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => openContainerAndCount(holding)}
                                  style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    background: "#4f46e5",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: busy ? "default" : "pointer",
                                    fontWeight: 600,
                                  }}
                                >
                                  {busy ? "Opening…" : "Open and count this job"}
                                </button>
                              </div>
                            );
                          })}

                        {containersHeld > 0 && usageContainers.length === 0 ? (
                          <p style={{ margin: "10px 0 0", fontSize: "12px", color: "#92400e" }}>
                            Finish the container in Service Inventory once it is empty — that is when
                            its cost is divided across the jobs listed here.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                )}
              </div>

              <div
                style={{
                  padding: "14px 20px",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  disabled={usageSaving}
                  onClick={() => setUsageBooking(null)}
                  style={{
                    padding: "10px 16px",
                    fontSize: "14px",
                    background: "white",
                    color: "#334155",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {usageHoldings.length === 0 ? "Close" : "Nothing to record"}
                </button>
                {usageHoldings.length > 0 ? (
                  <button
                    type="button"
                    disabled={usageSaving}
                    onClick={submitUsage}
                    style={{
                      padding: "10px 16px",
                      fontSize: "14px",
                      background: "#4f46e5",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {usageSaving ? "Saving…" : "Save"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* New Booking Modal */}
        {showNewBookingForm && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 1000,
              padding: window.innerWidth < 600 ? "12px" : "20px",
              overflowY: "auto",
              paddingTop: window.innerWidth < 600 ? "12px" : "40px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: window.innerWidth < 600 ? "16px" : "20px",
                padding: window.innerWidth < 600 ? "20px" : "32px",
                maxWidth: "600px",
                width: "100%",
                maxHeight:
                  window.innerWidth < 600
                    ? "calc(100vh - 24px)"
                    : "calc(100vh - 80px)",
                overflowY: "auto",
                margin: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                  paddingBottom: "16px",
                  borderBottom: "2px solid #f0f0f0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: window.innerWidth < 600 ? "18px" : "22px",
                    color: "#001d4a",
                    fontWeight: "600",
                  }}
                >
                  Create New Booking
                </h3>
                <button
                  type="button"
                  onClick={() => setShowNewBookingForm(false)}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#6b7280",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#e5e7eb";
                    e.target.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#f3f4f6";
                    e.target.style.transform = "scale(1)";
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleBookingSubmit}>
                {/* Service Selection Section */}
                <div
                  style={{
                    background: "#fafbff",
                    padding: "20px",
                    borderRadius: "16px",
                    marginBottom: "24px",
                    border: "1px solid #e5e9f2",
                  }}
                >
                  <div className="form-group" style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#1e293b",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Service *
                    </label>
                    <select
                      value={formData.serviceId}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        handleFormChange("serviceId", selectedId);
                      }}
                      required
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                        outline: "none",
                        background: "white",
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <option key="empty" value="">
                        Select a service
                      </option>
                      {services.map((service) => (
                        <option
                          key={service.id || service._id}
                          value={service.id || service._id}
                        >
                          {service.name} - ${service.price} (
                          {service.durationMinutes || service.duration} min)
                        </option>
                      ))}
                    </select>
                    {services.length === 0 && (
                      <small
                        style={{
                          color: "#dc2626",
                          marginTop: "8px",
                          display: "block",
                        }}
                      >
                        No active services found. Add services first.
                      </small>
                    )}
                  </div>

                  {/* Booking Type */}
                  <div className="form-group" style={{ marginBottom: "0" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#1e293b",
                        marginBottom: "8px",
                        display: "block",
                      }}
                    >
                      Booking Type *
                    </label>
                    <select
                      value={formData.bookingType}
                      onChange={(e) =>
                        handleFormChange("bookingType", e.target.value)
                      }
                      required
                      style={{
                        width: "100%",
                        padding: "14px 16px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                        outline: "none",
                        background: "white",
                        transition: "all 0.2s",
                        cursor: "pointer",
                      }}
                    >
                      <option key="walk-in" value="walk-in">
                        Walk-In
                      </option>
                      <option key="phone" value="phone">
                        Phone
                      </option>
                      <option key="manual" value="manual">
                        Manual Entry
                      </option>
                    </select>
                  </div>
                </div>

                {/* Customer Information Section */}
                <div
                  style={{
                    background: "white",
                    padding: "20px",
                    borderRadius: "16px",
                    border: "1px solid #e5e9f2",
                    marginBottom: "24px",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 20px 0",
                      fontSize: "16px",
                      color: "#1e293b",
                      fontWeight: "700",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    👤 Customer Details
                  </h4>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        window.innerWidth > 500 ? "1fr 1fr" : "1fr",
                      gap: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <div className="form-group" style={{ marginBottom: "0" }}>
                      <label
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "8px",
                          display: "block",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.customerFirstName}
                        onChange={(e) =>
                          handleFormChange("customerFirstName", e.target.value)
                        }
                        placeholder="John"
                        required
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          fontSize: "15px",
                          borderRadius: "10px",
                          border: "2px solid #e2e8f0",
                          outline: "none",
                          transition: "all 0.2s",
                        }}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: "0" }}>
                      <label
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "8px",
                          display: "block",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Last Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.customerLastName}
                        onChange={(e) =>
                          handleFormChange("customerLastName", e.target.value)
                        }
                        placeholder="Doe"
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          fontSize: "15px",
                          borderRadius: "10px",
                          border: "2px solid #e2e8f0",
                          outline: "none",
                          transition: "all 0.2s",
                        }}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "8px",
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.customerPhone}
                      onChange={(e) =>
                        handleFormChange("customerPhone", e.target.value)
                      }
                      placeholder="(555) 123-4567"
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                        outline: "none",
                        transition: "all 0.2s",
                      }}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: "0" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "8px",
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) =>
                        handleFormChange("customerEmail", e.target.value)
                      }
                      placeholder="customer@example.com"
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                        outline: "none",
                        transition: "all 0.2s",
                      }}
                    />
                  </div>
                </div>

                {/* Date & Time Section */}
                <div
                  style={{
                    background: "#fafbff",
                    padding: "20px",
                    borderRadius: "16px",
                    marginBottom: "24px",
                    border: "1px solid #e5e9f2",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 20px 0",
                      fontSize: "16px",
                      color: "#1e293b",
                      fontWeight: "700",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    📅 Date & Time
                  </h4>

                  {/* Appointment Date */}
                  <div className="form-group" style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "8px",
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Appointment Date *
                    </label>
                    <input
                      type="date"
                      value={formData.appointmentDate}
                      onChange={(e) =>
                        handleFormChange("appointmentDate", e.target.value)
                      }
                      min={(() => {
                        const today = new Date();
                        const year = today.getFullYear();
                        const month = String(today.getMonth() + 1).padStart(
                          2,
                          "0",
                        );
                        const day = String(today.getDate()).padStart(2, "0");
                        return `${year}-${month}-${day}`;
                      })()}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        fontSize: "15px",
                        borderRadius: "10px",
                        border: "2px solid #e2e8f0",
                        outline: "none",
                        background: "white",
                        transition: "all 0.2s",
                      }}
                    />
                  </div>

                  {/* Available Time Slots */}
                  {formData.serviceId && formData.appointmentDate && (
                    <div className="form-group" style={{ marginBottom: "0" }}>
                      <label
                        style={{
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#64748b",
                          marginBottom: "12px",
                          display: "block",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Available Time Slots *
                      </label>
                      {loadingSlots ? (
                        <p
                          style={{
                            color: "#64748b",
                            fontSize: "15px",
                            textAlign: "center",
                            padding: "20px",
                          }}
                        >
                          ⏳ Loading available slots...
                        </p>
                      ) : availableSlots.length > 0 ? (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              window.innerWidth < 600
                                ? "repeat(2, 1fr)"
                                : "repeat(3, 1fr)",
                            gap: "10px",
                            marginTop: "8px",
                            maxHeight: "300px",
                            overflowY: "auto",
                            padding: "4px",
                          }}
                        >
                          {availableSlots.map((slot, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() =>
                                handleFormChange("startTime", slot.time24)
                              }
                              style={{
                                padding: "12px 10px",
                                border:
                                  formData.startTime === slot.time24
                                    ? "2px solid #2d6da3"
                                    : "2px solid #e2e8f0",
                                borderRadius: "10px",
                                background:
                                  formData.startTime === slot.time24
                                    ? "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)"
                                    : "white",
                                color:
                                  formData.startTime === slot.time24
                                    ? "white"
                                    : "#334155",
                                cursor: "pointer",
                                fontSize: "14px",
                                fontWeight:
                                  formData.startTime === slot.time24
                                    ? "700"
                                    : "600",
                                transition: "all 0.2s ease",
                                whiteSpace: "nowrap",
                                boxShadow:
                                  formData.startTime === slot.time24
                                    ? "0 4px 12px rgba(102, 126, 234, 0.4)"
                                    : "none",
                              }}
                            >
                              {slot.time12}
                            </button>
                          ))}
                        </div>
                      ) : formData.appointmentDate &&
                        !isDateAvailable(formData.appointmentDate) ? (
                        <p
                          style={{
                            color: "#dc2626",
                            fontSize: "15px",
                            margin: "8px 0",
                            fontStyle: "italic",
                          }}
                        >
                          You're not available on this day. Check your schedule
                          settings.
                        </p>
                      ) : (
                        <p
                          style={{
                            color: "#dc2626",
                            fontSize: "15px",
                            margin: "8px 0",
                          }}
                        >
                          No available time slots for this date. All slots may
                          be booked.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Payment Method */}
                <div className="form-group" style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "13px",
                      color: "#64748b",
                      marginBottom: "8px",
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    💳 Payment Method
                  </label>
                  <select
                    value={formData.paymentMethod}
                    onChange={(e) =>
                      handleFormChange("paymentMethod", e.target.value)
                    }
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="online">Online</option>
                  </select>
                </div>

                {/* Tip Amount */}
                <div className="form-group" style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "13px",
                      color: "#64748b",
                      marginBottom: "8px",
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    💵 Tip Amount (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.tipAmount}
                    onChange={(e) =>
                      handleFormChange(
                        "tipAmount",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                      transition: "all 0.2s",
                    }}
                  />
                </div>

                {/* Customer Notes */}
                <div className="form-group" style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "13px",
                      color: "#64748b",
                      marginBottom: "8px",
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    📝 Notes (Optional)
                  </label>
                  <textarea
                    value={formData.customerNotes}
                    onChange={(e) =>
                      handleFormChange("customerNotes", e.target.value)
                    }
                    placeholder="Any special requests or notes..."
                    rows="3"
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                      transition: "all 0.2s",
                    }}
                  />
                </div>

                {/* Form Actions */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginTop: "32px",
                    paddingTop: "24px",
                    borderTop: "1px solid #e5e9f2",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewBookingForm(false);
                      setFormData({
                        serviceId: "",
                        bookingType: "walk-in",
                        customerFirstName: "",
                        customerLastName: "",
                        customerPhone: "",
                        customerEmail: "",
                        appointmentDate: "",
                        startTime: "",
                        customerNotes: "",
                        paymentMethod: "pending",
                        tipAmount: 0,
                      });
                      setAvailableSlots([]);
                      setErrorMsg("");
                    }}
                    style={{
                      flex: 1,
                      padding: "14px 24px",
                      fontSize: "15px",
                      fontWeight: "600",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      background: "white",
                      color: "#64748b",
                      cursor: submitting ? "not-allowed" : "pointer",
                      transition: "all 0.2s",
                      opacity: submitting ? 0.5 : 1,
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 2,
                      padding: "14px 24px",
                      fontSize: "15px",
                      fontWeight: "700",
                      border: "none",
                      borderRadius: "12px",
                      background:
                        submitting || !formData.serviceId || !formData.startTime
                          ? "#cbd5e1"
                          : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      color: "white",
                      cursor:
                        submitting || !formData.serviceId || !formData.startTime
                          ? "not-allowed"
                          : "pointer",
                      transition: "all 0.2s",
                      boxShadow:
                        submitting || !formData.serviceId || !formData.startTime
                          ? "none"
                          : "0 6px 20px rgba(102, 126, 234, 0.4)",
                      letterSpacing: "0.3px",
                    }}
                    disabled={
                      submitting || !formData.serviceId || !formData.startTime
                    }
                  >
                    {submitting ? "⏳ Creating..." : "✓ Create Booking"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Details Modal */}
        {showDetailsModal && selectedBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
            onClick={() => setShowDetailsModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "32px",
                maxWidth: "600px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                  paddingBottom: "16px",
                  borderBottom: "2px solid #f0f0f0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    color: "#001d4a",
                    fontWeight: "700",
                  }}
                >
                  Booking Details
                </h3>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#6b7280",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Status Badge */}
              <div
                style={{
                  padding: "8px 16px",
                  borderRadius: "12px",
                  fontSize: "14px",
                  fontWeight: "700",
                  background: (() => {
                    const statusColors = {
                      pending: { bg: "#fef3c7", text: "#92400e" },
                      confirmed: { bg: "#dbeafe", text: "#1e40af" },
                      completed: { bg: "#d1fae5", text: "#065f46" },
                      cancelled: { bg: "#fee2e2", text: "#991b1b" },
                      "no-show": { bg: "#f3f4f6", text: "#374151" },
                    };
                    return (
                      statusColors[selectedBooking.status]?.bg || "#f3f4f6"
                    );
                  })(),
                  color: (() => {
                    const statusColors = {
                      pending: { bg: "#fef3c7", text: "#92400e" },
                      confirmed: { bg: "#dbeafe", text: "#1e40af" },
                      completed: { bg: "#d1fae5", text: "#065f46" },
                      cancelled: { bg: "#fee2e2", text: "#991b1b" },
                      "no-show": { bg: "#f3f4f6", text: "#374151" },
                    };
                    return (
                      statusColors[selectedBooking.status]?.text || "#374151"
                    );
                  })(),
                  textTransform: "uppercase",
                  marginBottom: "24px",
                  display: "inline-block",
                }}
              >
                {selectedBooking.status}
              </div>

              {/* Details Grid */}
              <div style={{ display: "grid", gap: "20px" }}>
                {/* Booking Number */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    # Booking Number
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "700",
                    }}
                  >
                    {selectedBooking.bookingNumber || "-"}
                  </p>
                </div>

                {/* Service */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    💼 Service
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "600",
                    }}
                  >
                    {selectedBooking.serviceId?.name}
                  </p>
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {(() => {
                      const cancellationPayout =
                        getCancellationPayoutAmount(selectedBooking);
                      if (cancellationPayout !== null) {
                        return `Cancellation payout: $${cancellationPayout.toFixed(2)} • ${selectedBooking.duration} minutes`;
                      }
                      return `$${selectedBooking.servicePriceAtBooking} • ${selectedBooking.duration} minutes`;
                    })()}
                  </p>
                </div>

                {/* Customer */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    👤 Customer
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "600",
                    }}
                  >
                    {selectedBooking.customerId
                      ? `${selectedBooking.customerId.firstName} ${selectedBooking.customerId.lastName}`
                      : selectedBooking.customerName || "Walk-in Customer"}
                  </p>
                  {selectedBooking.customerPhone && (
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        margin: "4px 0 0 0",
                      }}
                    >
                      📞 {selectedBooking.customerPhone}
                    </p>
                  )}
                  {selectedBooking.customerEmail && (
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        margin: "4px 0 0 0",
                      }}
                    >
                      ✉️ {selectedBooking.customerEmail}
                    </p>
                  )}
                </div>

                {/* Date & Time */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    📅 Date & Time
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "600",
                    }}
                  >
                    {formatDate(selectedBooking.appointmentDate)}
                  </p>
                  {(() => {
                    const staffTz =
                      selectedBooking.staffTimezone ||
                      selectedBooking.shopTimezone ||
                      professionalTimezone;
                    const customerTz = selectedBooking.customerTimezone;
                    const appointmentDateStr = selectedBooking.appointmentDate; // Already YYYY-MM-DD string

                    if (customerTz && !isSameTimezone(staffTz, customerTz)) {
                      const staffTimeStart = formatTimeWithTimezone(
                        selectedBooking.startTime,
                        appointmentDateStr,
                        staffTz,
                      );
                      const staffTimeEnd = formatTimeWithTimezone(
                        selectedBooking.endTime,
                        appointmentDateStr,
                        staffTz,
                      );
                      const customerTimeStart = formatTimeWithTimezone(
                        selectedBooking.startTime,
                        appointmentDateStr,
                        customerTz,
                      );
                      const customerTimeEnd = formatTimeWithTimezone(
                        selectedBooking.endTime,
                        appointmentDateStr,
                        customerTz,
                      );

                      return (
                        <>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#1e293b",
                              margin: "4px 0 0 0",
                              fontWeight: "600",
                            }}
                          >
                            🕐 {staffTimeStart} - {staffTimeEnd}
                          </p>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#9ca3af",
                              margin: "4px 0 0 0",
                            }}
                          >
                            Customer sees: {customerTimeStart} -{" "}
                            {customerTimeEnd}
                          </p>
                        </>
                      );
                    }

                    return (
                      <p
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          margin: "4px 0 0 0",
                        }}
                      >
                        🕐 {formatTime12Hour(selectedBooking.startTime)} -{" "}
                        {formatTime12Hour(selectedBooking.endTime)}
                      </p>
                    );
                  })()}
                </div>

                {/* Booking Type */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    📝 Booking Type
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  >
                    {selectedBooking.bookingType}
                  </p>
                </div>

                {/* Payment Method */}
                <div>
                  <label
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      display: "block",
                      marginBottom: "8px",
                    }}
                  >
                    💳 Payment Method
                  </label>
                  <p
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      margin: 0,
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  >
                    {selectedBooking.paymentMethod || "Pending"}
                  </p>
                </div>

                {/* Tip Amount */}
                {selectedBooking.tipAmount > 0 && (
                  <div>
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      💵 Tip
                    </label>
                    <p
                      style={{
                        fontSize: "16px",
                        color: "#10b981",
                        margin: 0,
                        fontWeight: "600",
                      }}
                    >
                      ${selectedBooking.tipAmount.toFixed(2)}
                    </p>
                  </div>
                )}

                {/* Customer Notes */}
                {selectedBooking.customerNotes && (
                  <div>
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      💬 Customer Notes
                    </label>
                    <p
                      style={{
                        fontSize: "15px",
                        color: "#1e293b",
                        margin: 0,
                        padding: "12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                      }}
                    >
                      {selectedBooking.customerNotes}
                    </p>
                  </div>
                )}

                {/* Staff Notes */}
                {selectedBooking.staffNotes && (
                  <div>
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        marginBottom: "8px",
                      }}
                    >
                      🗒️ Staff Notes
                    </label>
                    <p
                      style={{
                        fontSize: "15px",
                        color: "#1e293b",
                        margin: 0,
                        padding: "12px",
                        background: "#f9fafb",
                        borderRadius: "8px",
                      }}
                    >
                      {selectedBooking.staffNotes}
                    </p>
                  </div>
                )}

                {/* Cancellation Reason */}
                {selectedBooking.status === "cancelled" &&
                  selectedBooking.cancellationReason && (
                    <div>
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        ❌ Cancellation Reason
                      </label>
                      <p
                        style={{
                          fontSize: "15px",
                          color: "#dc2626",
                          margin: 0,
                          padding: "12px",
                          background: "#fee2e2",
                          borderRadius: "8px",
                        }}
                      >
                        {selectedBooking.cancellationReason}
                      </p>
                    </div>
                  )}
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  width: "100%",
                  marginTop: "24px",
                  padding: "14px",
                  background: "#2d6da3",
                  color: "white",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "15px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Edit Booking Modal */}
        {showEditModal && selectedBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
              overflowY: "auto",
              paddingTop: "40px",
            }}
            onClick={() => setShowEditModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "20px",
                padding: "32px",
                maxWidth: "600px",
                width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                margin: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                  paddingBottom: "16px",
                  borderBottom: "2px solid #f0f0f0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "22px",
                    color: "#001d4a",
                    fontWeight: "700",
                  }}
                >
                  Edit Booking
                </h3>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#6b7280",
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleEditSubmit}>
                {/* Service Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#1e293b",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Service *
                  </label>
                  <select
                    value={editFormData.serviceId}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        serviceId: e.target.value,
                      });
                      // Refetch slots if date is selected
                      if (editFormData.appointmentDate) {
                        fetchAvailableSlots(
                          e.target.value,
                          editFormData.appointmentDate,
                        );
                      }
                    }}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                    }}
                  >
                    {services.map((service) => (
                      <option
                        key={service.id || service._id}
                        value={service.id || service._id}
                      >
                        {service.name} - ${service.price} (
                        {service.durationMinutes} min)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Appointment Date */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#1e293b",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Date *
                  </label>
                  <input
                    type="date"
                    value={editFormData.appointmentDate}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      const oldDate = editFormData.appointmentDate;

                      // Only clear startTime if date actually changed
                      if (newDate !== oldDate) {
                        setEditFormData({
                          ...editFormData,
                          appointmentDate: newDate,
                          startTime: "",
                        });
                        fetchAvailableSlots(editFormData.serviceId, newDate);
                      } else {
                        setEditFormData({
                          ...editFormData,
                          appointmentDate: newDate,
                        });
                      }
                    }}
                    min={new Date().toISOString().split("T")[0]}
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Available Time Slots */}
                {editFormData.serviceId && editFormData.appointmentDate && (
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "#1e293b",
                        marginBottom: "12px",
                        display: "block",
                      }}
                    >
                      Time Slot *
                    </label>
                    {loadingSlots ? (
                      <p
                        style={{
                          color: "#64748b",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        Loading slots...
                      </p>
                    ) : availableSlots.length > 0 ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: "10px",
                        }}
                      >
                        {availableSlots.map((slot, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() =>
                              setEditFormData({
                                ...editFormData,
                                startTime: slot.time24,
                              })
                            }
                            style={{
                              padding: "12px",
                              border:
                                editFormData.startTime === slot.time24
                                  ? "2px solid #2d6da3"
                                  : "2px solid #e2e8f0",
                              borderRadius: "10px",
                              background:
                                editFormData.startTime === slot.time24
                                  ? "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)"
                                  : "white",
                              color:
                                editFormData.startTime === slot.time24
                                  ? "white"
                                  : "#334155",
                              cursor: "pointer",
                              fontSize: "14px",
                              fontWeight:
                                editFormData.startTime === slot.time24
                                  ? "700"
                                  : "600",
                            }}
                          >
                            {slot.time12}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p
                        style={{
                          color: "#dc2626",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        No available slots
                      </p>
                    )}
                  </div>
                )}

                {/* Payment Method */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#1e293b",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    💳 Payment Method
                  </label>
                  <select
                    value={editFormData.paymentMethod}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        paymentMethod: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  >
                    <option value="pending">Pending</option>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="online">Online</option>
                  </select>
                </div>

                {/* Tip Amount */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#1e293b",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    💵 Tip Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editFormData.tipAmount}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        tipAmount: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Customer Notes */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#1e293b",
                      marginBottom: "8px",
                      display: "block",
                    }}
                  >
                    Notes
                  </label>
                  <textarea
                    value={editFormData.customerNotes}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        customerNotes: e.target.value,
                      })
                    }
                    rows="3"
                    style={{
                      width: "100%",
                      padding: "12px",
                      fontSize: "15px",
                      borderRadius: "10px",
                      border: "2px solid #e2e8f0",
                      outline: "none",
                      resize: "vertical",
                      fontFamily: "inherit",
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    style={{
                      flex: 1,
                      padding: "14px",
                      fontSize: "15px",
                      fontWeight: "600",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      background: "white",
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 2,
                      padding: "14px",
                      fontSize: "15px",
                      fontWeight: "700",
                      border: "none",
                      borderRadius: "12px",
                      background:
                        submitting || !editFormData.startTime
                          ? "#cbd5e1"
                          : "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                      color: "white",
                      cursor:
                        submitting || !editFormData.startTime
                          ? "not-allowed"
                          : "pointer",
                      boxShadow:
                        submitting || !editFormData.startTime
                          ? "none"
                          : "0 6px 20px rgba(102, 126, 234, 0.4)",
                    }}
                    disabled={submitting || !editFormData.startTime}
                  >
                    {submitting ? "⏳ Updating..." : "✓ Update Booking"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Hand-off Modal (2026-10-24) */}
        {showHandoffModal && handoffBooking && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: isMobile ? "12px" : "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: isMobile ? "20px" : "28px",
                maxWidth: "480px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
            >
              <h3 style={{ margin: "0 0 6px 0", color: "#001d4a" }}>
                🔄 Hand this booking off
              </h3>
              <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#6b7280" }}>
                The booking only moves when the professional you choose accepts.
              </p>

              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: "8px",
                  padding: "12px",
                  fontSize: "13px",
                  color: "#374151",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <strong>Service:</strong>{" "}
                  {handoffBooking.serviceId?.name || "Appointment"}
                </div>
                <div>
                  <strong>When:</strong> {handoffBooking.appointmentDate} at{" "}
                  {handoffBooking.startTime}
                </div>
              </div>

              {handoffError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#991b1b",
                    borderRadius: "8px",
                    padding: "10px",
                    fontSize: "13px",
                    marginBottom: "12px",
                  }}
                >
                  {handoffError}
                </div>
              )}

              {handoffBusy && handoffCandidates.length === 0 ? (
                <p style={{ fontSize: "13px", color: "#6b7280" }}>
                  Finding professionals who offer this service and are free…
                </p>
              ) : (
                <>
                  <label
                    htmlFor="handoff-target"
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "6px",
                    }}
                  >
                    Hand to
                  </label>
                  <select
                    id="handoff-target"
                    value={handoffTargetId}
                    onChange={(e) => setHandoffTargetId(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "14px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    <option value="">-- Select a professional --</option>
                    {handoffCandidates.map((candidate) => (
                      <option key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                  {handoffCandidates.length === 0 && !handoffBusy && (
                    <p style={{ fontSize: "12px", color: "#b45309" }}>
                      Nobody else offers this service and is free at that time,
                      so there is no one to hand it to. Request a cancellation
                      instead.
                    </p>
                  )}
                  <input
                    type="text"
                    value={handoffNote}
                    onChange={(e) => setHandoffNote(e.target.value)}
                    placeholder="Optional note for them"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "14px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      boxSizing: "border-box",
                    }}
                  />
                </>
              )}

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => {
                    setShowHandoffModal(false);
                    setHandoffBooking(null);
                  }}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitHandoff}
                  disabled={handoffBusy || !handoffTargetId}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background:
                      handoffBusy || !handoffTargetId ? "#9ca3af" : "#4338ca",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "600",
                    cursor:
                      handoffBusy || !handoffTargetId ? "not-allowed" : "pointer",
                  }}
                >
                  {handoffBusy ? "Sending…" : "Send hand-off"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Request Modal */}
        {showCancelRequestModal && cancelRequestBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: isMobile ? "12px" : "20px",
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: isMobile ? "20px" : "32px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                  paddingBottom: "16px",
                  borderBottom: "2px solid #f0f0f0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "18px" : "22px",
                    color: "#001d4a",
                    fontWeight: "600",
                  }}
                >
                  Request Cancellation
                </h3>
                <button
                  onClick={() => {
                    setShowCancelRequestModal(false);
                    setCancelRequestBooking(null);
                    setCancelRequestReason("");
                  }}
                  style={{
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#6b7280",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  padding: "16px",
                  background: "#fffbeb",
                  border: "1px solid #fbbf24",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <p style={{ margin: 0, fontSize: "13px", color: "#78350f" }}>
                  📋 <strong>Note:</strong> This will send a cancellation
                  request to the shop owner for approval. The owner can approve
                  or deny the request. If approved, they will process any
                  necessary refunds.
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "8px",
                  }}
                >
                  <strong>Booking Details:</strong>
                </p>
                <p style={{ fontSize: "14px", margin: "4px 0" }}>
                  Customer:{" "}
                  {cancelRequestBooking.customerId
                    ? `${cancelRequestBooking.customerId.firstName || ""} ${cancelRequestBooking.customerId.lastName || ""}`.trim() ||
                      cancelRequestBooking.customerName ||
                      "Unknown Customer"
                    : cancelRequestBooking.customerName || "Unknown Customer"}
                </p>
                <p style={{ fontSize: "14px", margin: "4px 0" }}>
                  Service:{" "}
                  {cancelRequestBooking.serviceId?.name || "Unknown Service"}
                </p>
                <p style={{ fontSize: "14px", margin: "4px 0" }}>
                  Date: {formatDate(cancelRequestBooking.appointmentDate)}
                </p>
                <p style={{ fontSize: "14px", margin: "4px 0" }}>
                  Time: {formatTime12Hour(cancelRequestBooking.startTime)}
                </p>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    fontSize: "14px",
                  }}
                >
                  Reason for Cancellation *
                </label>
                <textarea
                  value={cancelRequestReason}
                  onChange={(e) => setCancelRequestReason(e.target.value)}
                  placeholder="Please explain why you need to cancel this booking..."
                  style={{
                    width: "100%",
                    minHeight: "100px",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "Inter, sans-serif",
                    resize: "vertical",
                  }}
                  required
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <button
                  onClick={() => {
                    setShowCancelRequestModal(false);
                    setCancelRequestBooking(null);
                    setCancelRequestReason("");
                  }}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    border: "2px solid #e2e8f0",
                    borderRadius: "8px",
                    background: "white",
                    color: "#64748b",
                    cursor: "pointer",
                  }}
                  disabled={cancelRequestSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitCancelRequest}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "700",
                    border: "none",
                    borderRadius: "8px",
                    background: cancelRequestSubmitting ? "#cbd5e1" : "#fbbf24",
                    color: cancelRequestSubmitting ? "#94a3b8" : "#78350f",
                    cursor: cancelRequestSubmitting ? "not-allowed" : "pointer",
                    boxShadow: cancelRequestSubmitting
                      ? "none"
                      : "0 4px 12px rgba(251, 191, 36, 0.4)",
                  }}
                  disabled={
                    cancelRequestSubmitting || !cancelRequestReason.trim()
                  }
                >
                  {cancelRequestSubmitting
                    ? "⏳ Sending..."
                    : "📤 Submit Request"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Mark as Paid Modal */}
        {showMarkPaidModal && markPaidBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.6)",
              zIndex: 999999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}
            onClick={() => {
              if (!markPaidSubmitting) {
                setShowMarkPaidModal(false);
                setMarkPaidBooking(null);
                setMarkPaidData({ paymentType: "cash", amount: "", notes: "" });
              }
            }}
          >
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: "24px",
                  borderBottom: "1px solid #e2e8f0",
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  borderRadius: "16px 16px 0 0",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  💵 Mark Booking as Paid
                </h2>
                <p
                  style={{
                    margin: "8px 0 0",
                    fontSize: "14px",
                    color: "rgba(255, 255, 255, 0.9)",
                  }}
                >
                  Record cash or terminal payment for this booking
                </p>
              </div>

              {/* Modal Content */}
              <div style={{ padding: "24px" }}>
                {/* Booking Info */}
                <div
                  style={{
                    padding: "16px",
                    background: "#f8fafc",
                    borderRadius: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Customer:</strong>{" "}
                    {markPaidBooking.customerId
                      ? `${markPaidBooking.customerId.firstName || ""} ${markPaidBooking.customerId.lastName || ""}`.trim()
                      : markPaidBooking.customerName || "Walk-in Customer"}
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong>Service:</strong>{" "}
                    {markPaidBooking.serviceId?.name || "Unknown Service"}
                  </div>
                  <div>
                    <strong>Total Amount:</strong> $
                    {(
                      (markPaidBooking.servicePriceAtBooking || 0) +
                      (markPaidBooking.tipAmount || 0) +
                      (markPaidBooking.productsTotalPrice || 0)
                    ).toFixed(2)}
                  </div>
                </div>

                {/* Payment Type */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Payment Method *
                  </label>
                  <select
                    value={markPaidData.paymentType}
                    onChange={(e) =>
                      setMarkPaidData({
                        ...markPaidData,
                        paymentType: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    <option value="cash">💵 Cash</option>
                    <option value="terminal">💳 Card Terminal</option>
                    <option value="card_present">💳 Card Present</option>
                  </select>
                </div>

                {/* Amount */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Amount Received ($) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={markPaidData.amount}
                    onChange={(e) =>
                      setMarkPaidData({
                        ...markPaidData,
                        amount: e.target.value,
                      })
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Inter, sans-serif",
                    }}
                    required
                  />
                </div>

                {/* Notes */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    value={markPaidData.notes}
                    onChange={(e) =>
                      setMarkPaidData({
                        ...markPaidData,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Add any additional payment notes..."
                    style={{
                      width: "100%",
                      minHeight: "80px",
                      padding: "12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "Inter, sans-serif",
                      resize: "vertical",
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <button
                    onClick={() => {
                      setShowMarkPaidModal(false);
                      setMarkPaidBooking(null);
                      setMarkPaidData({
                        paymentType: "cash",
                        amount: "",
                        notes: "",
                      });
                    }}
                    style={{
                      flex: 1,
                      padding: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      background: "white",
                      color: "#64748b",
                      cursor: "pointer",
                    }}
                    disabled={markPaidSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkAsPaid}
                    style={{
                      flex: 1,
                      padding: "12px",
                      fontSize: "14px",
                      fontWeight: "700",
                      border: "none",
                      borderRadius: "8px",
                      background: markPaidSubmitting ? "#cbd5e1" : "#10b981",
                      color: "white",
                      cursor: markPaidSubmitting ? "not-allowed" : "pointer",
                      boxShadow: markPaidSubmitting
                        ? "none"
                        : "0 4px 12px rgba(16, 185, 129, 0.4)",
                    }}
                    disabled={
                      markPaidSubmitting ||
                      !markPaidData.amount ||
                      parseFloat(markPaidData.amount) <= 0
                    }
                  >
                    {markPaidSubmitting ? "⏳ Saving..." : "✓ Mark as Paid"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Migration Confirmation Modal */}
      {showMigrationModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
          onClick={() => setShowMigrationModal(false)}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: isMobile ? "24px" : "32px",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "20px",
                fontWeight: "700",
                color: "#111",
              }}
            >
              Migrate Bookings
            </h3>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: "14px",
                color: "#333",
                lineHeight: "1.6",
              }}
            >
              Migrate {migrationCount} bookings from your Owner profile to your
              Professional profile?
            </p>
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: "14px",
                color: "#333",
                lineHeight: "1.6",
              }}
            >
              This will move all bookings so they appear in your professional
              dashboard. This action cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowMigrationModal(false)}
                disabled={migrationLoading}
                style={{
                  padding: "10px 24px",
                  background: "#e5e7eb",
                  color: "#374151",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: migrationLoading ? "not-allowed" : "pointer",
                  opacity: migrationLoading ? 0.5 : 1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMigrateBookings}
                disabled={migrationLoading}
                style={{
                  padding: "10px 24px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: migrationLoading ? "not-allowed" : "pointer",
                  opacity: migrationLoading ? 0.7 : 1,
                }}
              >
                {migrationLoading ? "Migrating..." : "OK"}
              </button>
            </div>
          </div>
        </div>
      )}
    </StaffSidebarLayout>
  );
}

export default StaffBookings;
